import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Stethoscope, ShieldCheck, CalendarDays, FileEdit,
  Layers, Menu as MenuIcon, Megaphone, Phone, LogOut, Plus, Trash2, ArrowUp,
  ArrowDown, Pencil, Upload, Check, X, Eye, Home, MessageSquare, Contact, Camera,
  GripVertical, Linkedin, Mail, Database, ChevronDown, ChevronRight, ArrowLeft,
  FileText, UserMinus, UserX, RotateCcw, Copy, IdCard, Lock, ScrollText, ShieldAlert, Search, ArrowUpDown,
  Briefcase, BarChart3, BadgeIndianRupee, Wallet, Headset, Building2, Package2, Tag,
  BadgeCheck, Sparkles, MapPin as MapPinIc, Crown, Clock, Hospital, ExternalLink,
  Star, TrendingUp, Download,
} from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import BackToHome from "@/components/portal/BackToHome";
import Inbox from "@/components/portal/Inbox";
import AdminTicketInbox from "@/components/portal/AdminTicketInbox";
import NotificationsBell from "@/components/portal/NotificationsBell";
import { useMessaging } from "@/context/MessagingContext";
import { useKeyNav } from "@/lib/useKeyNav";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { BusinessPatients, BusinessDoctors, BusinessRevenue, BusinessPayments } from "@/pages/admin/Business";
import { B2BInquiries, B2BPartners, B2BCategories, B2BProducts, B2BRevenue, B2BPageContent, B2BOrders } from "@/pages/admin/B2B";

const NAV = [
  { type: "item", key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  {
    type: "group", key: "communications", label: "Communications", icon: MessageSquare, children: [
      { key: "inbox", label: "Inbox", icon: MessageSquare },
      { key: "comms", label: "Mail", icon: Megaphone },
    ],
  },
  { type: "item", key: "patients", label: "Patients", icon: Users },
  { type: "item", key: "doctors", label: "Doctors", icon: Stethoscope },
  { type: "item", key: "approvals", label: "Approvals", icon: ShieldCheck },
  { type: "item", key: "appointments", label: "Appointments", icon: CalendarDays },
  { type: "item", key: "reviews", label: "Reviews", icon: Star },
  { type: "item", key: "analytics", label: "Analytics", icon: TrendingUp },
  { type: "item", key: "support", label: "Customer support", icon: Headset },
  { type: "item", key: "security", label: "Account & security", icon: Lock },
  { type: "item", key: "clinical-audit", label: "Clinical audit logs", icon: ShieldAlert },
  {
    type: "group", key: "biz", label: "Business", icon: Briefcase, children: [
      { key: "biz-patients", label: "Patients", icon: Users },
      { key: "biz-doctors", label: "Doctors", icon: Stethoscope },
      { key: "biz-revenue", label: "Revenue", icon: BadgeIndianRupee },
      { key: "biz-payments", label: "Payments", icon: Wallet },
    ],
  },
  {
    type: "group", key: "b2b", label: "B2B", icon: Building2, children: [
      { key: "b2b-inquiries", label: "Inquiries", icon: Mail },
      { key: "b2b-orders", label: "Orders", icon: Package2 },
      { key: "b2b-partners", label: "Partners", icon: Briefcase },
      { key: "b2b-categories", label: "Categories", icon: Tag },
      { key: "b2b-products", label: "Products", icon: Package2 },
      { key: "b2b-revenue", label: "Revenue", icon: BadgeIndianRupee },
    ],
  },
  {
    type: "group", key: "site", label: "Website", icon: BarChart3, children: [
      { key: "content", label: "Website content", icon: FileEdit },
      { key: "about", label: "About us", icon: Contact },
      { key: "specialties", label: "Specialties", icon: Layers },
      { key: "header", label: "Header & services", icon: MenuIcon },
      { key: "b2b-content", label: "B2B & storefront pages", icon: FileEdit },
      { key: "callbacks", label: "Callbacks", icon: Phone },
      { key: "audit", label: "Audit log", icon: ScrollText },
    ],
  },
];

const NAV_FLAT = NAV.flatMap(n => n.type === "item" ? [n] : n.children.map(c => ({ ...c, parent: n.key })));

/* -------------------------------- Login ---------------------------------- */
function AdminLogin({ onDone }) {
  const { refresh } = useAuth();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const login = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      const u = await refresh();
      if (u?.role !== "admin") { setErr("Admin access only."); return; }
      onDone();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="adm"><div className="login-shell" style={{ gridColumn: "1 / -1" }}>
      <div style={{ position: "absolute", top: 24, left: 24 }}><BackToHome /></div>
      <div className="login-card">
        <div className="side-logo" style={{ margin: "0 auto 16px" }}>2</div>
        <h2 style={{ fontFamily: "Fraunces,serif", fontWeight: 540, textAlign: "center", margin: "0 0 4px" }}>Admin console</h2>
        <p style={{ color: "var(--slate)", fontSize: 13.5, textAlign: "center", margin: "0 0 22px" }}>2nd Opinion Health Consultants</p>
        {err && <p style={{ color: "var(--danger)", fontSize: 13 }} data-testid="admin-error">{err}</p>}
        <div className="field"><label>Email</label><input className="in" value={email} onChange={e => setEmail(e.target.value)} data-testid="admin-email" /></div>
        <div className="field"><label>Password</label><input className="in" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="admin-password" /></div>
        <button className="btn btn-ink" style={{ width: "100%", justifyContent: "center" }} onClick={login} disabled={busy} data-testid="admin-login">{busy ? "Signing in…" : "Sign in"}</button>
      </div>
    </div></div>
  );
}

/* --------------------- Forced first-login password change ---------------- */
const PW_HINT = "At least 12 characters with an uppercase, lowercase, number and symbol.";

function ForceChangePassword() {
  const { refresh, logout } = useAuth();
  const [cur, setCur] = useState(""); const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => {
    setErr("");
    if (pw !== pw2) { setErr("The two passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/admin/change-password", { current_password: cur, new_password: pw });
      await refresh();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="adm"><div className="login-shell" style={{ gridColumn: "1 / -1" }}>
      <div className="login-card" data-testid="admin-force-change">
        <div className="side-logo" style={{ margin: "0 auto 16px" }}>2</div>
        <h2 style={{ fontFamily: "Fraunces,serif", fontWeight: 540, textAlign: "center", margin: "0 0 4px" }}>Secure your account</h2>
        <p style={{ color: "var(--slate)", fontSize: 13.5, textAlign: "center", margin: "0 0 18px" }}>
          For security, set a new private password before you continue. Only you will ever know it.
        </p>
        {err && <p style={{ color: "var(--danger)", fontSize: 13 }} data-testid="force-change-error">{err}</p>}
        <div className="field"><label>Current password</label><input className="in" type="password" value={cur} onChange={e => setCur(e.target.value)} data-testid="force-current-password" /></div>
        <div className="field"><label>New password</label><input className="in" type="password" value={pw} onChange={e => setPw(e.target.value)} data-testid="force-new-password" /></div>
        <div className="field"><label>Confirm new password</label><input className="in" type="password" value={pw2} onChange={e => setPw2(e.target.value)} data-testid="force-confirm-password" /></div>
        <p style={{ color: "var(--slate)", fontSize: 12, margin: "0 0 14px" }}>{PW_HINT}</p>
        <button className="btn btn-ink" style={{ width: "100%", justifyContent: "center" }} onClick={submit} disabled={busy} data-testid="force-change-submit">{busy ? "Saving…" : "Set new password"}</button>
        <a onClick={logout} style={{ display: "block", textAlign: "center", marginTop: 14, fontSize: 13, color: "var(--slate)", cursor: "pointer" }} data-testid="force-change-signout">Sign out</a>
      </div>
    </div></div>
  );
}

/* ----------------------- Admin account & security ------------------------ */
function AccountSecurity() {
  const { user, refresh } = useAuth();
  const [eCur, setECur] = useState(""); const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [pCur, setPCur] = useState(""); const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const changeEmail = async () => {
    if (!newEmail.trim()) { toast("Enter a new email."); return; }
    setEmailBusy(true);
    try {
      await api.post("/auth/admin/change-email", { current_password: eCur, new_email: newEmail.trim() });
      await refresh();
      toast("Login email updated ✓");
      setECur(""); setNewEmail("");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setEmailBusy(false); }
  };

  const changePassword = async () => {
    if (pw !== pw2) { toast("The two passwords do not match."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/admin/change-password", { current_password: pCur, new_password: pw });
      await refresh();
      toast("Password updated ✓");
      setPCur(""); setPw(""); setPw2("");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  return (
    <div className="panel" data-testid="admin-security" style={{ maxWidth: 560 }}>
      <h3>Account &amp; security</h3>
      <p className="sub">Your login email and password. The password is stored only as an encrypted hash — it is never shown again and cannot be recovered by anyone, so keep it safe.</p>

      <div style={{ marginTop: 20 }}>
        <h4 style={{ margin: "0 0 8px" }}>Login email</h4>
        <p className="sub" style={{ marginTop: 0 }}>Current: <strong>{user?.email}</strong></p>
        <div className="field"><label>Current password</label><input className="in" type="password" value={eCur} onChange={e => setECur(e.target.value)} data-testid="security-email-current-password" /></div>
        <div className="field"><label>New email</label><input className="in" value={newEmail} onChange={e => setNewEmail(e.target.value)} data-testid="security-new-email" /></div>
        <button className="btn btn-ink btn-sm" onClick={changeEmail} disabled={emailBusy} data-testid="security-change-email">{emailBusy ? "Updating…" : "Update email"}</button>
      </div>

      <div style={{ marginTop: 28, borderTop: "1px solid var(--line, #e6e6e6)", paddingTop: 20 }}>
        <h4 style={{ margin: "0 0 8px" }}>Password</h4>
        <div className="field"><label>Current password</label><input className="in" type="password" value={pCur} onChange={e => setPCur(e.target.value)} data-testid="security-current-password" /></div>
        <div className="field"><label>New password</label><input className="in" type="password" value={pw} onChange={e => setPw(e.target.value)} data-testid="security-new-password" /></div>
        <div className="field"><label>Confirm new password</label><input className="in" type="password" value={pw2} onChange={e => setPw2(e.target.value)} data-testid="security-confirm-password" /></div>
        <p style={{ color: "var(--slate)", fontSize: 12, margin: "0 0 12px" }}>{PW_HINT}</p>
        <button className="btn btn-ink btn-sm" onClick={changePassword} disabled={pwBusy} data-testid="security-change-password">{pwBusy ? "Updating…" : "Update password"}</button>
      </div>
    </div>
  );
}

/* ------------------------------- Sections -------------------------------- */
function Dashboard({ onNavigate }) {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/stats").then(r => setS(r.data)); }, []);
  const cards = [
    ["patients", "Patients", "patients"],
    ["doctors", "Doctors", "doctors"],
    ["appointments", "Appointments", "appointments"],
    ["pending_doctors", "Pending approvals", "approvals"],
    ["specialties", "Specialties", "specialties"],
    ["callbacks", "New callbacks", "callbacks"],
  ];
  return (
    <div className="cards" data-testid="admin-stats">
      {cards.map(([k, l, target]) => (
        <button className="stat stat-link" key={k} onClick={() => onNavigate(target)} data-testid={`stat-${target}`} title={`Go to ${l}`}>
          <div className="n">{s[k] ?? "—"}</div><div className="l">{l}</div>
        </button>
      ))}
    </div>
  );
}

/* ----------------------- Pt. Reg Approval (CS unverified) ----------------------- */
function PatientApprovals({ onChange }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/admin/patient-approvals")
    .then(r => { setRows(r.data.pending || []); setLoaded(true); })
    .catch(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => {
    try { const d = new Date(iso); return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  const approve = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/admin/patient-approvals/${p.id}/approve`);
      toast(`Approved ✓ — Patient ID ${data.patient_code} assigned`);
      load(); onChange && onChange();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const reject = async (p) => {
    if (!window.confirm(`Reject the registration for ${p.name}? They will not be added to the platform.`)) return;
    setBusy(p.id);
    try {
      await api.post(`/admin/patient-approvals/${p.id}/reject`);
      toast("Registration rejected");
      load(); onChange && onChange();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((p) => [p.name, p.email, p.mobile].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
    : rows;

  return (
    <div data-testid="patient-approvals">
      <div className="panel-head">
        <div>
          <h3>Patient registration approvals</h3>
          <p className="sub">Unverified registrations from customer support · {rows.length} awaiting approval · a Patient ID is assigned only on approval</p>
        </div>
      </div>

      <div className="patient-search-bar">
        <Search size={15} className="patient-search-ic" />
        <input
          type="text"
          className="patient-search-input"
          placeholder="Search by name, email ID or phone number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="approvals-search-input"
        />
        {search && <button type="button" className="patient-search-clear" onClick={() => setSearch("")} data-testid="approvals-search-clear">✕</button>}
      </div>

      <table data-testid="approvals-table">
        <thead><tr>
          <th>Sl. No.</th><th>Date</th><th>Name</th><th>Age/Sex</th><th>Email ID</th><th>Phone number</th><th>Registered by</th><th>Actions</th>
        </tr></thead>
        <tbody>
          {filtered.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: "center", padding: "28px 0", color: "var(--slate)" }} data-testid="approvals-empty">
              {!loaded ? "Loading…" : (q ? `No registrations match “${search}”.` : "No pending registrations. ✓")}
            </td></tr>
          )}
          {filtered.map((p, i) => (
            <tr key={p.id} data-testid={`approval-row-${p.id}`}>
              <td>{i + 1}</td>
              <td>{fmtDate(p.created_at)}</td>
              <td><b>{p.name}</b></td>
              <td>{[p.age, p.sex].filter(Boolean).join(" / ") || "—"}</td>
              <td>{p.email || "—"}</td>
              <td>{p.mobile || "—"}</td>
              <td><span className="sub" style={{ fontSize: 12 }}>{p.registered_by_name || "—"}{p.registered_by_no ? ` (${p.registered_by_no})` : ""}</span></td>
              <td>
                <span className="row-actions">
                  <button className="btn btn-green btn-sm" disabled={busy === p.id} onClick={() => approve(p)} data-testid={`approval-approve-${p.id}`}><Check size={13} /> {busy === p.id ? "…" : "Approve"}</button>
                  <button className="btn btn-ghost btn-sm" disabled={busy === p.id} onClick={() => reject(p)} data-testid={`approval-reject-${p.id}`}><X size={13} /> Reject</button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Patients() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("patient_code");
  const [sortDir, setSortDir] = useState("asc");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", mobile: "", age: "", sex: "" });
  const [editP, setEditP] = useState(null);   // patient being edited
  const [editForm, setEditForm] = useState({ name: "", email: "", mobile: "", age: "", sex: "" });
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("list"); // list | approvals
  const [pendingCount, setPendingCount] = useState(0);

  const load = () => api.get("/admin/patients").then(r => setRows(r.data.patients || []));
  const loadPendingCount = () => api.get("/admin/patient-approvals")
    .then(r => setPendingCount((r.data.pending || []).length)).catch(() => {});
  useEffect(() => { load(); loadPendingCount(); }, []);

  const copy = (t) => { try { navigator.clipboard.writeText(t); toast("Patient ID copied ✓"); } catch { /* ignore */ } };

  const addPatient = async () => {
    if (!addForm.name.trim()) { toast("Patient name is required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/patients", addForm);
      toast("Patient added ✓ — ID auto-assigned");
      setShowAdd(false); setAddForm({ name: "", email: "", mobile: "", age: "", sex: "" });
      load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = (p) => { setEditP(p); setEditForm({ name: p.name || "", email: p.email || "", mobile: p.mobile || "", age: p.age || "", sex: p.sex || "" }); };
  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast("Name is required."); return; }
    setBusy(true);
    try {
      await api.put(`/admin/patients/${editP.id}`, editForm);
      toast("Patient updated ✓");
      setEditP(null); load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deletePatient = async (p) => {
    if (!window.confirm(`Permanently delete ${p.name}?\n\nThis erases ALL their data (appointments, uploaded documents, messages, family members). The Patient ID ${p.patient_code} will be retained as "Patient Deleted" and never reused. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/patients/${p.id}`);
      toast("Patient and all their data deleted ✓");
      load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fmtRegDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "—"; }
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "created_at" ? "desc" : "asc");
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <ArrowUpDown size={12} className="th-sort-ic" />;
    return sortDir === "asc"
      ? <ArrowUp size={12} className="th-sort-ic active" />
      : <ArrowDown size={12} className="th-sort-ic active" />;
  };

  const exportCsv = (data) => {
    const headers = ["Sl. No.", "Patient ID", "Date of registration", "Name", "Age", "Sex", "Email", "Phone number"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(",")];
    data.forEach((p, i) => {
      lines.push([
        i + 1,
        p.patient_code || "",
        fmtRegDate(p.created_at),
        p.deleted ? "Patient Deleted" : (p.name || ""),
        p.deleted ? "" : (p.age || ""),
        p.deleted ? "" : (p.sex || ""),
        p.deleted ? "" : (p.email || ""),
        p.deleted ? "" : (p.mobile || ""),
      ].map(esc).join(","));
    });
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `patients_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast(`Exported ${data.length} patient${data.length === 1 ? "" : "s"} to CSV ✓`);
  };

  return (
    <div className="panel">
      <div className="ptreg-tabs" data-testid="patients-view-tabs">
        <button className={`ptreg-tab ${view === "list" ? "on" : ""}`} onClick={() => setView("list")} data-testid="patients-tab-registered">
          <Users size={15} /> Registered patients
        </button>
        <button className={`ptreg-tab ${view === "subscribed" ? "on" : ""}`} onClick={() => setView("subscribed")} data-testid="patients-tab-subscribed">
          <BadgeCheck size={15} /> Subscribed patients
          {rows.filter(p => p.subscribed && !p.deleted).length > 0 && (
            <span className="ptreg-badge" style={{ background: "#1D9E75" }} data-testid="patients-subscribed-badge">
              {rows.filter(p => p.subscribed && !p.deleted).length}
            </span>
          )}
        </button>
        <button className={`ptreg-tab ${view === "approvals" ? "on" : ""}`} onClick={() => setView("approvals")} data-testid="patients-tab-approvals">
          <ShieldAlert size={15} /> Pt. Reg Approval
          {pendingCount > 0 && <span className="ptreg-badge" data-testid="patients-approval-badge">{pendingCount}</span>}
        </button>
      </div>

      {view === "approvals" && (
        <PatientApprovals onChange={() => { load(); loadPendingCount(); }} />
      )}

      {(view === "list" || view === "subscribed") && (<>
      <div className="panel-head">
        <div>
          <h3>{view === "subscribed" ? "Subscribed patients" : "Registered patients"}</h3>
          <p className="sub">
            {view === "subscribed"
              ? `${rows.filter(p => p.subscribed && !p.deleted).length} premium subscribers · automatic discount applied at booking`
              : `${rows.length} total · ordered by Patient ID · Patient ID is system-assigned and not editable`}
          </p>
        </div>
        <button className="btn btn-blue" onClick={() => setShowAdd(true)} data-testid="add-patient-open"><Plus size={15} /> Add patient</button>
      </div>
      <div className="patient-search-bar">
        <Search size={15} className="patient-search-ic" />
        <input
          type="text"
          className="patient-search-input"
          placeholder="Search by name, Patient ID, mobile number or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="patients-search-input"
        />
        {search && (
          <button
            type="button"
            className="patient-search-clear"
            onClick={() => setSearch("")}
            title="Clear search"
            data-testid="patients-search-clear"
          >✕</button>
        )}
      </div>
      {(() => {
        const q = search.trim().toLowerCase();
        const baseRows = view === "subscribed" ? rows.filter((p) => p.subscribed && !p.deleted) : rows;
        const matched = q
          ? baseRows.filter((p) => {
              if (p.deleted) {
                return (p.patient_code || "").toLowerCase().includes(q) || "patient deleted".includes(q);
              }
              return [p.name, p.patient_code, p.email, p.mobile]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q));
            })
          : baseRows;

        const sortVal = (p) => {
          switch (sortKey) {
            case "patient_code": return (p.patient_code || "~").toLowerCase();
            case "created_at": return p.created_at || "";
            case "name": return (p.deleted ? "zzz patient deleted" : (p.name || "")).toLowerCase();
            case "age": {
              const n = parseInt(p.age, 10);
              return Number.isNaN(n) ? -1 : n;
            }
            case "sex": return (p.sex || "").toLowerCase();
            case "email": return (p.email || "").toLowerCase();
            case "mobile": return (p.mobile || "").toLowerCase();
            default: return "";
          }
        };
        const filtered = [...matched].sort((a, b) => {
          const va = sortVal(a), vb = sortVal(b);
          if (va < vb) return sortDir === "asc" ? -1 : 1;
          if (va > vb) return sortDir === "asc" ? 1 : -1;
          return 0;
        });

        const sortHeader = (k, label) => (
          <th key={k}>
            <button
              type="button"
              className={`th-sort ${sortKey === k ? "on" : ""}`}
              onClick={() => toggleSort(k)}
              data-testid={`patients-sort-${k}`}
            >
              {label} {sortIcon(k)}
            </button>
          </th>
        );

        return (
          <>
            <div className="patients-toolbar-row">
              {q && (
                <p className="sub" style={{ margin: 0 }} data-testid="patients-search-count">
                  {filtered.length} match{filtered.length === 1 ? "" : "es"} for “{search}”
                </p>
              )}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => exportCsv(filtered)}
                disabled={filtered.length === 0}
                data-testid="patients-export-csv"
              >
                <FileText size={14} /> Export CSV
              </button>
            </div>
            <table data-testid="patients-table">
              <thead><tr>
                <th>Sl. No.</th>
                {sortHeader("patient_code", "Patient ID")}
                {sortHeader("created_at", "Date of registration")}
                {sortHeader("name", "Name")}
                {sortHeader("age", "Age/Sex")}
                {sortHeader("email", "Email ID")}
                {sortHeader("mobile", "Phone number")}
                <th>Actions</th>
              </tr></thead>
              <tbody>{filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: "28px 0", color: "var(--slate)" }} data-testid="patients-empty">
                  {q ? `No patients match “${search}”.` : "No patients yet."}
                </td></tr>
              )}{filtered.map((p, i) => (
          <tr key={p.id} className={p.deleted ? "row-deleted" : ""}>
            <td>{i + 1}</td>
            <td>
              <span className="pid-cell">
                <span className={`pid-chip ${p.subscribed && !p.deleted ? "subscribed" : ""}`} data-testid={`patient-code-${p.id}`}>{p.patient_code || "—"}</span>
                <button className="pid-mini" title="Copy" onClick={() => copy(p.patient_code)} data-testid={`patient-code-copy-${p.id}`}><Copy size={12} /></button>
                <Lock size={12} style={{ color: "var(--slate)" }} title="Patient ID cannot be edited" />
                {p.subscribed && !p.deleted && <span className="sub-tag" data-testid={`patient-sub-tag-${p.id}`}><BadgeCheck size={11} /> Premium</span>}
              </span>
            </td>
            <td data-testid={`patient-regdate-${p.id}`}>{fmtRegDate(p.created_at)}</td>
            {p.deleted ? (
              <>
                <td colSpan={3}><span className="deleted-badge" data-testid={`patient-deleted-${p.id}`}>Patient Deleted</span></td>
              </>
            ) : (
              <>
                <td>{p.name}{p.relationship && <span className="rel-chip" style={{ marginLeft: 6 }}>{p.relationship}</span>}</td>
                <td>{[p.age, p.sex].filter(Boolean).join(" / ") || "—"}</td>
                <td>{p.email || "—"}</td>
                <td>{p.mobile || "—"}</td>
              </>
            )}
            <td>
              {p.deleted ? <span className="sub" style={{ fontSize: 12 }}>—</span> : (
                <span className="row-actions">
                  <button className="pid-mini" title="Edit details" onClick={() => openEdit(p)} data-testid={`patient-edit-${p.id}`}><Pencil size={13} /></button>
                  <button className="pid-mini danger" title="Delete patient" onClick={() => deletePatient(p)} data-testid={`patient-delete-${p.id}`}><Trash2 size={13} /></button>
                </span>
              )}
            </td>
          </tr>
        ))}</tbody>
      </table>
          </>
        );
      })()}
      </>)}


      {showAdd && (
        <div className="modal-bg" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} data-testid="add-patient-modal">
            <button className="m-close" onClick={() => setShowAdd(false)}>✕</button>
            <h3 className="h" style={{ fontSize: 20, marginTop: 0 }}>Add a patient</h3>
            <p className="lead" style={{ marginBottom: 14 }}>A unique Patient ID is auto-assigned by the system in registration order.</p>
            <div className="field"><label>Full name</label><input className="in" value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} data-testid="add-patient-name" /></div>
            <div className="field two">
              <div><label>Age</label><input className="in" value={addForm.age} onChange={e => setAddForm({ ...addForm, age: e.target.value })} data-testid="add-patient-age" /></div>
              <div><label>Sex</label>
                <select className="in" value={addForm.sex} onChange={e => setAddForm({ ...addForm, sex: e.target.value })} data-testid="add-patient-sex">
                  <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                </select></div>
            </div>
            <div className="field"><label>Email (optional)</label><input className="in" value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} data-testid="add-patient-email" /></div>
            <div className="field"><label>Phone number</label><input className="in" value={addForm.mobile} onChange={e => setAddForm({ ...addForm, mobile: e.target.value })} data-testid="add-patient-mobile" /></div>
            <button className="btn btn-green full" onClick={addPatient} disabled={busy} data-testid="add-patient-submit">{busy ? "Adding…" : "Add patient"}</button>
          </div>
        </div>
      )}

      {editP && (
        <div className="modal-bg" onClick={() => setEditP(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} data-testid="edit-patient-modal">
            <button className="m-close" onClick={() => setEditP(null)}>✕</button>
            <h3 className="h" style={{ fontSize: 20, marginTop: 0 }}>Edit patient</h3>
            <div className="prof-row"><span className="prof-k">Patient ID</span><span className="prof-v"><span className={`pid-chip ${editP.subscribed ? "subscribed" : ""}`}>{editP.patient_code}</span> <Lock size={12} style={{ color: "var(--slate)" }} /></span></div>
            <p className="prof-hint" style={{ marginBottom: 14 }}>Patient ID is system-assigned and cannot be changed.</p>
            <div className="field"><label>Full name</label><input className="in" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-patient-name" /></div>
            <div className="field two">
              <div><label>Age</label><input className="in" value={editForm.age} onChange={e => setEditForm({ ...editForm, age: e.target.value })} data-testid="edit-patient-age" /></div>
              <div><label>Sex</label>
                <select className="in" value={editForm.sex} onChange={e => setEditForm({ ...editForm, sex: e.target.value })} data-testid="edit-patient-sex">
                  <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                </select></div>
            </div>
            <div className="field"><label>Email ID</label><input className="in" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-patient-email" /></div>
            <div className="field"><label>Phone number</label><input className="in" value={editForm.mobile} onChange={e => setEditForm({ ...editForm, mobile: e.target.value })} data-testid="edit-patient-mobile" /></div>
            <div className="field" style={{ background: editP.subscribed ? "#e3f5ec" : "#f3f5f8", border: `1px solid ${editP.subscribed ? "#bfe3d2" : "#dce7f2"}`, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: editP.subscribed ? "#0F6E56" : "var(--ink)" }}>
                <Crown size={15} color={editP.subscribed ? "#1D9E75" : "#46607A"} />
                <b>Premium subscriber</b>
                {editP.subscribed && <span className="sub-tag" data-testid="edit-patient-sub-tag"><BadgeCheck size={11} /> Active</span>}
              </span>
              <button
                type="button"
                className={`btn btn-sm ${editP.subscribed ? "btn-ghost" : "btn-green"}`}
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.patch(`/admin/patients/${editP.id}/subscription`, { subscribed: !editP.subscribed });
                    toast(editP.subscribed ? "Subscription removed" : "Subscription activated ✓");
                    const next = { ...editP, subscribed: !editP.subscribed };
                    setEditP(next);
                    load();
                  } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
                  finally { setBusy(false); }
                }}
                data-testid="edit-patient-sub-toggle"
              >
                {editP.subscribed ? "Deactivate" : "Activate subscription"}
              </button>
            </div>
            <button className="btn btn-green full" onClick={saveEdit} disabled={busy} data-testid="edit-patient-submit">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const DOC_FIELDS = [
  ["id", "Identity proof"],
  ["degree", "Degree certificate"],
  ["mbbsreg", "MBBS registration"],
  ["pgdeg", "PG degree"],
  ["pgreg", "PG registration"],
  ["ssdeg", "Superspecialty degree"],
  ["ssreg", "Superspecialty registration"],
];
const DOC_LABEL = Object.fromEntries(DOC_FIELDS);

function Doctors() {
  const [subview, setSubview] = useState("home");
  const [selId, setSelId] = useState(null);
  const openProfile = (id) => { setSelId(id); setSubview("profile"); };
  if (subview === "profile") return <DoctorProfileAdmin id={selId} onBack={() => setSubview("home")} />;
  if (subview === "database") return <DoctorDatabase onOpen={openProfile} onBack={() => setSubview("home")} />;
  return <DoctorsHome onOpenProfile={openProfile} onOpenDatabase={() => setSubview("database")} />;
}

function DoctorsHome({ onOpenProfile, onOpenDatabase }) {
  const [rows, setRows] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const empty = { name: "", email: "", mobile: "", specialty: "", reg_no: "", password: "", qualifications: "", experience_years: "", languages: "", consult_fee: "", bio: "" };
  const [form, setForm] = useState(empty);
  const [docs, setDocs] = useState({});
  const [busy, setBusy] = useState(false);
  const [openSpec, setOpenSpec] = useState(null);
  const [subFilter, setSubFilter] = useState("all"); // "all" | "subscribed"
  const load = () => api.get("/admin/doctors").then(r => setRows(r.data.doctors || []));
  useEffect(() => { load(); api.get("/public/specialties").then(r => setSpecialties(r.data.specialties || [])); }, []);
  const add = async () => {
    if (!form.name || !form.email) { toast("Full name and email are required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      const { data } = await api.post("/admin/doctors", fd);
      const docEntries = Object.entries(docs).filter(([, f]) => f);
      if (docEntries.length) {
        const dfd = new FormData();
        docEntries.forEach(([t, f]) => { dfd.append("files", f); dfd.append("types", t); });
        await api.post(`/admin/doctors/${data.id}/documents`, dfd);
      }
      toast("Doctor added ✓"); setForm(empty); setDocs({}); load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const working = rows.filter(d => !["removed", "resigned"].includes(d.doctor_state || "working"));
  const visible = subFilter === "subscribed" ? working.filter(d => d.subscribed) : working;
  const groups = {};
  visible.forEach(d => { const s = d.specialty || "Unspecified"; (groups[s] = groups[s] || []).push(d); });
  const specNames = Object.keys(groups).sort();
  const subscribedCount = working.filter(d => d.subscribed).length;

  // ---- Search the working panel by name / NMC (alphanumeric) / mobile / email ----
  const [q, setQ] = useState("");
  const fmtRegDate = (iso) => {
    if (!iso) return "—";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  const normNmc = (v) => (v || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
  const ql = q.trim().toLowerCase();
  const qn = normNmc(q);
  const matches = ql === "" ? [] : working.filter(d => {
    const name = (d.name || "").toLowerCase();
    const email = (d.email || "").toLowerCase();
    const mobile = (d.mobile || "").toLowerCase();
    return name.includes(ql) || email.includes(ql) || mobile.includes(ql) ||
      (qn !== "" && normNmc(d.reg_no).includes(qn));
  });
  const suggestList = matches.slice(0, 10);
  const { activeIndex, setActiveIndex, onKeyDown, listRef } = useKeyNav(
    suggestList, (d) => { setQ(""); onOpenProfile(d.id); }, { onEscape: () => setQ("") }
  );

  return (
    <>
      <div className="dochead">
        <div><h3 style={{ margin: 0 }}>Doctors</h3><p className="sub" style={{ margin: "2px 0 0" }}>Add, organise and manage your specialist panel</p></div>
        <button className="btn btn-green" onClick={onOpenDatabase} data-testid="doctor-database-btn"><Database size={15} /> Doctor database</button>
      </div>

      <div className="panel">
        <h3>Add a doctor manually</h3><p className="sub">Add the same details &amp; documents a doctor provides at sign-up. Default password Doctor@123 if left blank.</p>
        <div className="row2">
          <div className="field"><label>Full name *</label><input className="in" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="md-name" /></div>
          <div className="field"><label>Email *</label><input className="in" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="md-email" /></div>
        </div>
        <div className="row2">
          <div className="field"><label>Mobile</label><input className="in" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="md-mobile" /></div>
          <div className="field"><label>Specialty</label>
            <select className="in" value={form.specialty} onChange={e => setForm({ ...form, specialty: e.target.value })} data-testid="md-specialty">
              <option value="">Select a specialty</option>
              {specialties.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="row2">
          <div className="field"><label>NMC Registration number</label><input className="in" value={form.reg_no} onChange={e => setForm({ ...form, reg_no: e.target.value })} placeholder="e.g. KMC 119333" data-testid="md-regno" /></div>
          <div className="field"><label>Temporary password</label><input className="in" placeholder="Doctor@123" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="md-password" /></div>
        </div>
        <div className="row2">
          <div className="field"><label>Qualifications</label><input className="in" placeholder="MBBS, MD…" value={form.qualifications} onChange={e => setForm({ ...form, qualifications: e.target.value })} data-testid="md-quals" /></div>
          <div className="field"><label>Experience</label><input className="in" placeholder="e.g. 12 years" value={form.experience_years} onChange={e => setForm({ ...form, experience_years: e.target.value })} data-testid="md-exp" /></div>
        </div>
        <div className="row2">
          <div className="field"><label>Languages</label><input className="in" placeholder="English, Hindi…" value={form.languages} onChange={e => setForm({ ...form, languages: e.target.value })} /></div>
          <div className="field"><label>Consultation fee</label><input className="in" placeholder="₹999" value={form.consult_fee} onChange={e => setForm({ ...form, consult_fee: e.target.value })} /></div>
        </div>
        <div className="field"><label>About / bio</label><textarea className="in" rows={3} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} /></div>
        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Documents (optional)</label>
        <div className="docgrid">
          {DOC_FIELDS.map(([t, label]) => (
            <label className={`docslot ${docs[t] ? "has" : ""}`} key={t}>
              <span className="docslot-l">{label}</span>
              <span className="docslot-f">{docs[t]?.name || "Upload file"}</span>
              <input type="file" hidden onChange={e => setDocs({ ...docs, [t]: e.target.files[0] })} data-testid={`md-doc-${t}`} />
            </label>
          ))}
        </div>
        <button className="btn btn-green" onClick={add} disabled={busy} data-testid="md-add"><Plus size={15} /> {busy ? "Adding…" : "Add doctor"}</button>
      </div>

      <div className="panel">
        <h3>All doctors</h3>
        <p className="sub">
          {subFilter === "subscribed"
            ? `${visible.length} premium-subscribed doctor${visible.length === 1 ? "" : "s"} grouped by specialty`
            : `${working.length} on the panel · search below, or browse grouped by specialty`}
        </p>

        <div className="biz-tabs" style={{ marginBottom: 14 }} data-testid="doctors-sub-filter">
          <button
            className={subFilter === "all" ? "on" : ""}
            onClick={() => { setSubFilter("all"); setOpenSpec(null); }}
            data-testid="doctors-filter-all"
          >
            All doctors
          </button>
          <button
            className={subFilter === "subscribed" ? "on" : ""}
            onClick={() => { setSubFilter("subscribed"); setOpenSpec(null); }}
            data-testid="doctors-filter-subscribed"
          >
            <BadgeCheck size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} />
            Subscribed doctors
            {subscribedCount > 0 && <span style={{ marginLeft: 6, background: "#1D9E75", color: "#fff", borderRadius: 999, padding: "0 7px", fontSize: 11, fontWeight: 700 }}>{subscribedCount}</span>}
          </button>
        </div>

        <div className="biz-search-wrap" style={{ marginBottom: 14 }}>
          <div className="patient-search-bar" style={{ maxWidth: 640, margin: 0 }}>
            <Search size={15} className="patient-search-ic" />
            <input
              className="patient-search-input"
              placeholder="Search by name, NMC number, mobile or email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded={suggestList.length > 0}
              aria-activedescendant={activeIndex >= 0 && suggestList[activeIndex] ? `docsearch-suggest-${suggestList[activeIndex].id}` : undefined}
              data-testid="doctors-search-input"
            />
            {q && <button className="patient-search-clear" onClick={() => setQ("")} data-testid="doctors-search-clear">✕</button>}
          </div>
          {ql !== "" && (
            <div className="biz-suggest" data-testid="doctors-search-suggest" role="listbox" ref={listRef}>
              {matches.length === 0 && <div className="biz-suggest-empty">No doctors match “{q}”.</div>}
              {suggestList.map((d, i) => (
                <button
                  key={d.id}
                  type="button"
                  id={`docsearch-suggest-${d.id}`}
                  role="option"
                  aria-selected={i === activeIndex}
                  className={`biz-suggest-row ${i === activeIndex ? "active" : ""}`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => { setQ(""); onOpenProfile(d.id); }}
                  data-testid={`doctors-search-suggest-${d.id}`}
                >
                  <span className="ib-avatar doctor"><Stethoscope size={14} /></span>
                  <span className="biz-suggest-main">
                    <b>{d.name}</b>
                    <small>{d.reg_no || "—"}{d.specialty ? " · " + d.specialty : ""}{d.mobile ? " · " + d.mobile : ""}{d.email ? " · " + d.email : ""}</small>
                  </span>
                </button>
              ))}
              {matches.length > 10 && <div className="biz-suggest-more">+ {matches.length - 10} more · refine your search to narrow down</div>}
              {suggestList.length > 0 && <div className="biz-suggest-hint">↑ ↓ to move · ↵ to open · esc to clear</div>}
            </div>
          )}
        </div>

        {specNames.length === 0 && <p className="sub">No doctors yet.</p>}
        {specNames.map(s => (
          <div className="specgroup" key={s} data-testid={`docgroup-${s}`}>
            <button className="specgroup-h" onClick={() => setOpenSpec(openSpec === s ? null : s)} data-testid={`docgroup-toggle-${s}`}>
              {openSpec === s ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span className="specgroup-name">{s}</span>
              <span className="specgroup-count">{groups[s].length}</span>
            </button>
            {openSpec === s && (
              <div className="specgroup-body">
                <table className="docspec-table" data-testid={`docspec-table-${s}`}>
                  <thead>
                    <tr>
                      <th>Date of registration</th>
                      <th>Doctor name</th>
                      <th>NMC number</th>
                      <th>Phone number</th>
                      <th>Email ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups[s].map(d => (
                      <tr className="row-click" key={d.id} onClick={() => onOpenProfile(d.id)} data-testid={`docname-${d.id}`}>
                        <td>{fmtRegDate(d.created_at)}</td>
                        <td>
                          <span className="docspec-name">
                            <span className="docnamerow-av sm">{d.photo_id ? <img src={`${API}/public/image/${d.photo_id}`} alt="" /> : d.name.replace("Dr. ", "").charAt(0)}</span>
                            <b>{d.name}</b>
                            {d.subscribed && <span className="sub-tag" style={{ marginLeft: 6 }} data-testid={`doctor-sub-tag-${d.id}`}><BadgeCheck size={10} /> Premium</span>}
                          </span>
                        </td>
                        <td><span className="pid-chip">{d.reg_no || "—"}</span></td>
                        <td>{d.mobile || "—"}</td>
                        <td>{d.email || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function DoctorDatabase({ onOpen, onBack }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("active");
  useEffect(() => { api.get("/admin/doctors/database").then(r => setData(r.data)); }, []);
  if (!data) return <div className="panel"><p className="sub">Loading…</p></div>;
  const tabs = [["active", "Active doctors", data.active], ["inactive", "Inactive doctors", data.inactive], ["resigned", "Resigned doctors", data.resigned], ["removed", "Removed doctors", data.removed]];
  const list = tabs.find(t => t[0] === tab)[2];
  const desc = {
    active: `Taking ${data.threshold} or more appointments per week`,
    inactive: `Taking fewer than ${data.threshold} appointments per week`,
    resigned: "Voluntarily discontinued from the platform",
    removed: "Removed by an admin (reason on record)",
  };
  return (
    <>
      <button className="back" onClick={onBack} data-testid="db-back"><ArrowLeft size={15} /> Back to doctors</button>
      <div className="panel">
        <h3>Doctor database</h3><p className="sub">{desc[tab]}</p>
        <div className="dbtabs">
          {tabs.map(([k, label, arr]) => (
            <button key={k} className={`dbtab ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} data-testid={`dbtab-${k}`}>
              {label} <span className="dbtab-c">{arr.length}</span>
            </button>
          ))}
        </div>
        {list.length === 0 ? <p className="sub" style={{ marginTop: 16 }}>No doctors in this category.</p> : (
          <div style={{ marginTop: 10 }}>
            {list.map(d => (
              <div className="listrow" key={d.id} data-testid={`db-doc-${d.id}`}>
                <div className="docnamerow-av" style={{ marginRight: 12 }}>{d.photo_id ? <img src={`${API}/public/image/${d.photo_id}`} alt="" /> : d.name.replace("Dr. ", "").charAt(0)}</div>
                <div className="m">
                  <h4>{d.name}</h4>
                  <p>{d.specialty || "—"}
                    {(tab === "active" || tab === "inactive") ? ` · ${d.appointments_week} appt/week` : ""}
                    {tab === "removed" && d.removal_reason ? ` · Reason: ${d.removal_reason}` : ""}
                    {tab === "resigned" && d.resignation_reason ? ` · ${d.resignation_reason}` : ""}
                  </p>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => onOpen(d.id)} data-testid={`db-open-${d.id}`}>View profile</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function DoctorProfileAdmin({ id, onBack }) {
  const [d, setD] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [reason, setReason] = useState("");
  const [clinics, setClinics] = useState([]);
  const [showClinics, setShowClinics] = useState(false);
  const load = () => {
    api.get(`/admin/doctors/${id}`).then(r => setD(r.data.doctor));
    api.get(`/admin/doctors/${id}/clinics`).then(r => setClinics(r.data.clinics || [])).catch(() => setClinics([]));
  };
  useEffect(() => { load(); api.get("/public/specialties").then(r => setSpecialties(r.data.specialties || [])); /* eslint-disable-next-line */ }, [id]);
  if (!d) return <div className="panel"><p className="sub">Loading…</p></div>;
  const startEdit = () => { setForm({ name: d.name || "", specialty: d.specialty || "", reg_no: d.reg_no || "", mobile: d.mobile || "", qualifications: d.qualifications || "", experience_years: d.experience_years || "", languages: d.languages || "", consult_fee: d.consult_fee || "", bio: d.bio || "" }); setPhoto(null); setEditing(true); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/doctors/${id}/profile`, form);
      if (photo) { const fd = new FormData(); fd.append("file", photo); await api.post(`/admin/doctors/${id}/photo`, fd); }
      toast("Profile updated ✓"); setEditing(false); await load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggleSubscription = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/doctors/${id}/subscription`, { subscribed: !d.subscribed });
      toast(d.subscribed ? "Subscription removed" : "Subscription activated ✓");
      await load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const startClinicEdit = () => {};
  const decideClinic = async (cid, action) => {
    try {
      await api.post(`/admin/doctors/${id}/clinics/${cid}/decision`, { action });
      toast(action === "approve" ? "Establishment approved ✓ — now live to patients" : "Establishment rejected");
      await load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const editClinicFee = async (cid, fee) => {
    try {
      await api.put(`/admin/doctors/${id}/clinics/${cid}`, { fee: String(fee || "") });
      toast("Clinic fee updated ✓");
      await load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const doRemove = async () => {
    if (!reason.trim()) { toast("Please enter a reason for removal"); return; }
    try { await api.post(`/admin/doctors/${id}/remove`, { reason }); toast("Doctor removed"); setShowRemove(false); setReason(""); await load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const doResign = async () => { try { await api.post(`/admin/doctors/${id}/resign`, { reason: "" }); toast("Marked as resigned"); await load(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); } };
  const doReinstate = async () => { try { await api.post(`/admin/doctors/${id}/reinstate`); toast("Doctor reinstated ✓"); await load(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); } };
  const doPreauth = async () => { try { await api.post(`/admin/doctors/${id}/preauthorize`); toast("Re-registration pre-authorised ✓"); await load(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); } };
  const state = d.doctor_state || "working";
  const stateLabel = { working: "Active on panel", removed: "Removed", resigned: "Resigned" }[state];
  const facts = [["Email", d.email], ["Mobile", d.mobile], ["NMC Registration number", d.reg_no], ["Qualifications", d.qualifications], ["Experience", d.experience_years], ["Languages", d.languages], ["Consultation fee", d.consult_fee]];
  return (
    <>
      <button className="back" onClick={onBack} data-testid="dpa-back"><ArrowLeft size={15} /> Back</button>
      {!editing ? (
        <div className="panel" data-testid="admin-doctor-profile">
          <div className="dpa-head">
            <div className="dpa-photo">{d.photo_id ? <img src={`${API}/public/image/${d.photo_id}`} alt="" /> : <Stethoscope size={30} />}</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0 }}>{d.name}</h3>
              <p className="sub" style={{ margin: "2px 0 6px" }}>{d.specialty || "—"} · {d.reg_no || "—"}</p>
              <span className={`tag ${state === "working" ? "ok" : "red"}`}>{stateLabel}</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {d.subscribed && (
                <button className="btn btn-green btn-sm" onClick={() => setShowClinics(s => !s)} data-testid="dpa-approve-clinic-btn">
                  <Building2 size={13} /> Approve Clinic
                  {clinics.filter(c => c.approval_status === "pending").length > 0 && (
                    <span className="dpa-clinic-badge" data-testid="dpa-clinic-pending-badge">{clinics.filter(c => c.approval_status === "pending").length}</span>
                  )}
                </button>
              )}
              <button className="btn btn-ink btn-sm" onClick={startEdit} data-testid="dpa-edit-btn"><Pencil size={13} /> Edit</button>
            </div>
          </div>
          {state === "removed" && <div className="banner warn" style={{ marginTop: 14 }}>Removed {d.removed_at ? `on ${String(d.removed_at).slice(0, 10)}` : ""}. Reason: {d.removal_reason || "—"}{d.reauth_allowed ? " · Re-registration pre-authorised ✓" : ""}</div>}
          {state === "resigned" && <div className="banner info" style={{ marginTop: 14 }}>Resigned {d.resigned_at ? `on ${String(d.resigned_at).slice(0, 10)}` : ""}. This doctor can re-register freely at any time.</div>}
          <div className="dpa-facts">
            {facts.map(([l, v]) => <div className="dpa-fact" key={l}><span>{l}</span><strong>{v || "—"}</strong></div>)}
          </div>

          {/* ----- Premium subscription + clinic ----- */}
          <div className="clinic-block" data-testid="dpa-subscription-block">
            <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Crown size={16} color={d.subscribed ? "#1D9E75" : "#46607A"} />
              Premium subscription
              {d.subscribed && <span className="sub-tag" data-testid="dpa-sub-active"><BadgeCheck size={11} /> Active</span>}
            </h4>
            <p className="sub">
              {d.subscribed
                ? "Subscribed doctors appear in patient clinic-search and can host an in-person clinic profile below."
                : "Activate Premium to publish this doctor on the patient clinic-search and add an in-person clinic profile."}
            </p>
            <button
              type="button"
              className={`btn btn-sm ${d.subscribed ? "btn-ghost" : "btn-green"}`}
              onClick={toggleSubscription}
              disabled={busy}
              data-testid="dpa-sub-toggle"
            >
              {d.subscribed ? "Deactivate subscription" : "Activate subscription"}
            </button>
          </div>

          {/* ----- Establishments approval (only when subscribed) ----- */}
          {d.subscribed && showClinics && (
            <div className="clinic-block" data-testid="dpa-clinics-block">
              <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Building2 size={16} /> Establishments ({clinics.length})
              </h4>
              <p className="sub">Doctor-managed clinics & hospitals. Approve each one to make it visible to premium patients in clinic-search. Personal contact details are never shown to patients — only the establishment's own phone/email.</p>
              {clinics.length === 0 && <p className="sub" style={{ fontStyle: "italic" }}>This doctor hasn't added any establishment yet.</p>}
              {clinics.map((c) => {
                const stColor = c.approval_status === "approved" ? "ok" : c.approval_status === "rejected" ? "red" : "pend";
                return (
                  <div className="dpa-clinic-row" key={c.id} data-testid={`dpa-clinic-${c.id}`}>
                    <div className="dpa-clinic-main">
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {c.establishment_type === "Hospital" ? <Hospital size={15} /> : <Building2 size={15} />}
                        <strong>{c.name}</strong>
                        <span className="estab-type-chip">{c.establishment_type}</span>
                        <span className={`tag ${stColor}`} data-testid={`dpa-clinic-status-${c.id}`}>{c.approval_status}</span>
                      </div>
                      <div className="dpa-clinic-facts">
                        {c.address && <span><MapPinIc size={12} /> {c.address}{c.pincode ? ` · ${c.pincode}` : ""}</span>}
                        {c.phone && <span><Phone size={12} /> {c.phone}</span>}
                        {c.email && <span><Mail size={12} /> {c.email}</span>}
                        {c.hours && <span><Clock size={12} /> {c.hours}</span>}
                        {c.maps_url && <a href={c.maps_url} target="_blank" rel="noreferrer"><ExternalLink size={12} /> Map</a>}
                      </div>
                      <div className="dpa-clinic-fee" data-testid={`dpa-clinic-fee-row-${c.id}`}>
                        <label>Consultation fee ₹</label>
                        <input className="in" defaultValue={String(c.fee || "").replace(/\D/g, "")} data-testid={`dpa-clinic-fee-input-${c.id}`}
                          onBlur={(e) => editClinicFee(c.id, e.target.value)} placeholder="e.g. 1200" />
                        <span className="dpa-fee-hint">editable by admin · auto-applies subscriber discount</span>
                      </div>
                      {(c.photos || []).length > 0 && (
                        <div className="dpa-clinic-photos">
                          {c.photos.map((fid) => <img key={fid} src={`${API}/public/image/${fid}`} alt="" />)}
                        </div>
                      )}
                      {Object.keys(c.slots || {}).filter(day => (c.slots[day] || []).length).length > 0 && (
                        <div className="dpa-clinic-slots">
                          {Object.keys(c.slots).filter(day => (c.slots[day] || []).length).map(day => (
                            <span key={day} className="estab-slot-chip">{day}: {(c.slots[day] || []).join(", ")}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="dpa-clinic-actions">
                      {c.approval_status !== "approved" && (
                        <button className="btn btn-green btn-sm" onClick={() => decideClinic(c.id, "approve")} data-testid={`dpa-clinic-approve-${c.id}`}><Check size={13} /> Approve</button>
                      )}
                      {c.approval_status !== "rejected" && (
                        <button className="btn btn-danger btn-sm" onClick={() => decideClinic(c.id, "reject")} data-testid={`dpa-clinic-reject-${c.id}`}><X size={13} /> Reject</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {d.bio && <div style={{ marginTop: 14 }}><label style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>About</label><p className="sub" style={{ whiteSpace: "pre-wrap" }}>{d.bio}</p></div>}
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Documents</label>
            {(d.documents || []).length === 0 ? <p className="sub">No documents on file.</p> : (
              <div className="doclist">
                {d.documents.map((doc, i) => (
                  <a className="docchip" key={i} href={`${API}/files/${doc.file_id}`} target="_blank" rel="noreferrer" data-testid={`dpa-doc-${i}`}><FileText size={14} /> {DOC_LABEL[doc.type] || doc.type}</a>
                ))}
              </div>
            )}
          </div>
          <div className="dpa-actions">
            {state === "working" && <>
              <button className="btn btn-ghost btn-sm" onClick={doResign} data-testid="dpa-resign"><UserMinus size={13} /> Mark resigned</button>
              <button className="btn btn-danger btn-sm" onClick={() => setShowRemove(true)} data-testid="dpa-remove-btn"><UserX size={13} /> Remove doctor</button>
            </>}
            {state !== "working" && <button className="btn btn-ink btn-sm" onClick={doReinstate} data-testid="dpa-reinstate"><RotateCcw size={13} /> Reinstate doctor</button>}
            {state === "removed" && !d.reauth_allowed && <button className="btn btn-ghost btn-sm" onClick={doPreauth} data-testid="dpa-preauth"><ShieldCheck size={13} /> Pre-authorise re-registration</button>}
          </div>
        </div>
      ) : (
        <div className="panel" data-testid="admin-doctor-edit">
          <h3>Edit profile — {d.name}</h3>
          <div className="about-photo">
            <div className="about-photo-prev">{photo ? <img src={URL.createObjectURL(photo)} alt="" /> : d.photo_id ? <img src={`${API}/public/image/${d.photo_id}`} alt="" /> : <Camera size={26} />}</div>
            <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}><input type="file" accept="image/*" hidden onChange={e => setPhoto(e.target.files[0])} data-testid="dpa-photo" /><Upload size={13} /> {photo ? photo.name : "Change photo"}</label>
          </div>
          <div className="row2">
            <div className="field"><label>Full name</label><input className="in" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="dpa-f-name" /></div>
            <div className="field"><label>Specialty</label><select className="in" value={form.specialty} onChange={e => setForm({ ...form, specialty: e.target.value })} data-testid="dpa-f-specialty"><option value="">Select</option>{specialties.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}</select></div>
          </div>
          <div className="row2">
            <div className="field"><label>NMC Registration number</label><input className="in" value={form.reg_no} onChange={e => setForm({ ...form, reg_no: e.target.value })} placeholder="e.g. KMC 119333" /></div>
            <div className="field"><label>Mobile</label><input className="in" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} /></div>
          </div>
          <div className="row2">
            <div className="field"><label>Qualifications</label><input className="in" value={form.qualifications} onChange={e => setForm({ ...form, qualifications: e.target.value })} /></div>
            <div className="field"><label>Experience</label><input className="in" value={form.experience_years} onChange={e => setForm({ ...form, experience_years: e.target.value })} /></div>
          </div>
          <div className="row2">
            <div className="field"><label>Languages</label><input className="in" value={form.languages} onChange={e => setForm({ ...form, languages: e.target.value })} /></div>
            <div className="field"><label>Consultation fee</label><input className="in" value={form.consult_fee} onChange={e => setForm({ ...form, consult_fee: e.target.value })} /></div>
          </div>
          <div className="field"><label>About / bio</label><textarea className="in" rows={4} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} data-testid="dpa-f-bio" /></div>
          <div className="about-footer">
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}><X size={13} /> Cancel</button>
            <button className="btn btn-ink" onClick={save} disabled={busy} data-testid="dpa-save">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </div>
      )}
      {showRemove && (
        <div className="modal-overlay" onClick={() => setShowRemove(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} data-testid="remove-modal">
            <h3 style={{ marginTop: 0 }}>Remove {d.name}</h3>
            <p className="sub">A reason is required and stored on record. The doctor will be hidden from the public site and moved to “Removed doctors”.</p>
            <div className="field"><label>Reason for removal *</label><textarea className="in" rows={3} value={reason} onChange={e => setReason(e.target.value)} data-testid="remove-reason" placeholder="e.g. Credentials could not be verified" /></div>
            <div className="about-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowRemove(false); setReason(""); }}>Cancel</button>
              <button className="btn btn-danger" onClick={doRemove} data-testid="remove-confirm"><UserX size={13} /> Remove doctor</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Approvals() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/doctors/pending").then(r => setRows(r.data.pending || []));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.patch(`/admin/doctors/${id}/approval`, { action }); toast(action === "approve" ? "Doctor approved ✓" : "Doctor rejected"); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="panel">
      <h3>Pending verifications</h3><p className="sub">{rows.length} awaiting review</p>
      {rows.length === 0 && <p className="sub">No pending applications.</p>}
      {rows.map(d => (
        <div className="listrow" key={d.id} data-testid={`pending-${d.id}`}>
          <div className="m">
            <h4>{d.name} <span className="tag pend">pending</span></h4>
            <p>{d.specialty} · {d.reg_no} · {d.email}</p>
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {(d.documents || []).map((doc, i) => (
                <button className="btn btn-ghost btn-sm" key={i} onClick={() => window.open(`${API}/files/${doc.file_id}`, "_blank")} data-testid={`pending-doc-${i}`}>
                  <Eye size={12} /> {doc.type}
                </button>
              ))}
            </div>
          </div>
          <div className="acts">
            <button className="btn btn-green btn-sm" onClick={() => act(d.id, "approve")} data-testid={`approve-${d.id}`}><Check size={13} /> Approve</button>
            <button className="btn btn-danger btn-sm" onClick={() => act(d.id, "reject")} data-testid={`reject-${d.id}`}><X size={13} /> Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const APPT_STATUSES = ["upcoming", "completed", "pending", "missed"];
const STATUS_LABEL = { upcoming: "Upcoming", completed: "Completed", pending: "Pending (doctor missed)", missed: "Missed (patient missed)", cancelled: "Cancelled" };

function fmtApptDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
const toYMD = (d) => {
  if (!d) return "";
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

function Appointments() {
  const [rows, setRows] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({ patient_name: "", phone: "", specialty: "", consult_type: "Video consultation", date: "", time: "10:00", doctor_id: "" });

  // search (independent of the filters below)
  const [q, setQ] = useState("");
  // sort
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  // filters
  const [fStatus, setFStatus] = useState("all");
  const [fPatient, setFPatient] = useState("all");
  const [fDoctor, setFDoctor] = useState("all");
  const [dateMode, setDateMode] = useState("single"); // single | range
  const [dateSingle, setDateSingle] = useState(undefined);
  const [dateRange, setDateRange] = useState(undefined); // {from, to}
  const [calOpen, setCalOpen] = useState(false);

  const load = () => api.get("/admin/appointments").then(r => setRows(r.data.appointments || []));
  useEffect(() => { load(); api.get("/admin/doctors").then(r => setDoctors(r.data.doctors || [])); }, []);
  const add = async () => {
    try { await api.post("/admin/appointments", form); toast("Appointment created ✓"); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const overrideStatus = async (id, status) => {
    try { await api.patch(`/admin/appointments/${id}/status`, { status }); toast("Status updated ✓"); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  // distinct patients / doctors that actually appear in the appointment list
  const patientOpts = [...new Set(rows.map(a => a.patient_name).filter(Boolean))].sort();
  const doctorOpts = [...new Set(rows.map(a => a.doctor_name).filter(Boolean))].sort();

  // ---- SEARCH is independent: when there's a query, it alone drives the list ----
  const searching = q.trim() !== "";
  const ql = q.trim().toLowerCase();
  const normNmc = (v) => (v || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
  const matchSearch = (a) => {
    const hay = [a.patient_name, a.patient_code, a.doctor_name, a.specialty, a.date, STATUS_LABEL[a.status], a.status]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(ql) || (normNmc(q) !== "" && normNmc(a.doctor_reg_no).includes(normNmc(q)));
  };

  // ---- FILTERS (used only when not searching) ----
  const inDateFilter = (a) => {
    if (dateMode === "single") {
      if (!dateSingle) return true;
      return a.date === toYMD(dateSingle);
    }
    if (!dateRange?.from) return true;
    const from = toYMD(dateRange.from);
    const to = toYMD(dateRange.to || dateRange.from);
    return a.date >= from && a.date <= to;
  };
  const matchFilters = (a) =>
    (fStatus === "all" || a.status === fStatus) &&
    (fPatient === "all" || a.patient_name === fPatient) &&
    (fDoctor === "all" || a.doctor_name === fDoctor) &&
    inDateFilter(a);

  let view = rows.filter(searching ? matchSearch : matchFilters);

  // ---- SORT ----
  const cmp = (a, b) => {
    let av, bv;
    if (sort.key === "date") { av = `${a.date || ""} ${a.time || ""}`; bv = `${b.date || ""} ${b.time || ""}`; }
    else if (sort.key === "specialty") { av = (a.specialty || "").toLowerCase(); bv = (b.specialty || "").toLowerCase(); }
    else if (sort.key === "patient") { av = (a.patient_name || "").toLowerCase(); bv = (b.patient_name || "").toLowerCase(); }
    else if (sort.key === "doctor") { av = (a.doctor_name || "").toLowerCase(); bv = (b.doctor_name || "").toLowerCase(); }
    if (av < bv) return sort.dir === "asc" ? -1 : 1;
    if (av > bv) return sort.dir === "asc" ? 1 : -1;
    return 0;
  };
  view = [...view].sort(cmp);

  const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "date" ? "desc" : "asc" });

  const dateBtnLabel = dateMode === "single"
    ? (dateSingle ? fmtApptDate(toYMD(dateSingle)) : "Any date")
    : (dateRange?.from ? `${fmtApptDate(toYMD(dateRange.from))} → ${dateRange.to ? fmtApptDate(toYMD(dateRange.to)) : "…"}` : "Any range");
  const clearFilters = () => { setFStatus("all"); setFPatient("all"); setFDoctor("all"); setDateSingle(undefined); setDateRange(undefined); };

  return (
    <>
      <div className="panel">
        <h3>Create appointment manually</h3><p className="sub">For phone or walk-in bookings</p>
        <div className="row2">
          <div className="field"><label>Patient name</label><input className="in" value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} data-testid="ma-name" /></div>
          <div className="field"><label>Phone</label><input className="in" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
        <div className="row2">
          <div className="field"><label>Doctor</label>
            <select className="in" value={form.doctor_id} onChange={e => setForm({ ...form, doctor_id: e.target.value, specialty: doctors.find(d => d.id === e.target.value)?.specialty || "" })} data-testid="ma-doctor">
              <option value="">Select</option>{doctors.map(d => <option key={d.id} value={d.id}>{d.name} ({d.specialty})</option>)}
            </select>
          </div>
          <div className="field"><label>Type</label>
            <select className="in" value={form.consult_type} onChange={e => setForm({ ...form, consult_type: e.target.value })}>
              <option>Video consultation</option><option>Written second opinion</option>
            </select>
          </div>
        </div>
        <div className="row2">
          <div className="field"><label>Date</label><input className="in" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="ma-date" /></div>
          <div className="field"><label>Time</label><input className="in" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></div>
        </div>
        <button className="btn btn-ink" onClick={add} data-testid="ma-add"><Plus size={15} /> Create appointment</button>
      </div>

      <div className="panel">
        <h3>All appointments</h3>
        <p className="sub">{rows.length} total · search by patient, Patient ID, doctor, NMC, date, specialty or status — or use the filters below</p>

        {/* SEARCH (independent) */}
        <div className="patient-search-bar" style={{ maxWidth: 720, marginBottom: 14 }}>
          <Search size={15} className="patient-search-ic" />
          <input
            className="patient-search-input"
            placeholder="Search appointments by patient, Patient ID, doctor, NMC, date, specialty or status…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="appt-search-input"
          />
          {q && <button className="patient-search-clear" onClick={() => setQ("")} data-testid="appt-search-clear">✕</button>}
        </div>

        {/* FILTERS (independent of search; shown disabled while searching) */}
        <div className="appt-filters" style={searching ? { opacity: 0.5, pointerEvents: "none" } : {}} data-testid="appt-filters">
          <div className="appt-filter" style={{ flex: "1 1 100%" }}>
            <label>Status</label>
            <div className="appt-status-pills">
              <button className={`appt-status-pill ${fStatus === "all" ? "on all" : ""}`} onClick={() => setFStatus("all")} data-testid="appt-filter-status-all">All</button>
              {APPT_STATUSES.map(s => (
                <button key={s} className={`appt-status-pill ${fStatus === s ? "on " + s : ""}`} onClick={() => setFStatus(s)} data-testid={`appt-filter-status-${s}`}>
                  {s === "pending" ? "Pending (doctor missed)" : s === "missed" ? "Missed (patient missed)" : STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
          <div className="appt-filter">
            <label>Patient</label>
            <select className="in" value={fPatient} onChange={e => setFPatient(e.target.value)} data-testid="appt-filter-patient">
              <option value="all">All patients</option>
              {patientOpts.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="appt-filter">
            <label>Doctor</label>
            <select className="in" value={fDoctor} onChange={e => setFDoctor(e.target.value)} data-testid="appt-filter-doctor">
              <option value="all">All doctors</option>
              {doctorOpts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="appt-filter">
            <label>Date</label>
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button className="appt-date-btn" data-testid="appt-filter-date-btn"><CalendarDays size={15} /> {dateBtnLabel}</button>
              </PopoverTrigger>
              <PopoverContent className="appt-cal-pop" align="start">
                <div className="appt-cal-modes">
                  <button className={dateMode === "single" ? "on" : ""} onClick={() => { setDateMode("single"); setDateRange(undefined); }} data-testid="appt-cal-mode-single">Single date</button>
                  <button className={dateMode === "range" ? "on" : ""} onClick={() => { setDateMode("range"); setDateSingle(undefined); }} data-testid="appt-cal-mode-range">Date range</button>
                </div>
                {dateMode === "single"
                  ? <Calendar mode="single" selected={dateSingle} onSelect={setDateSingle} />
                  : <Calendar mode="range" selected={dateRange} onSelect={setDateRange} numberOfMonths={1} />}
                <div style={{ display: "flex", gap: 8, padding: 8 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setDateSingle(undefined); setDateRange(undefined); }} data-testid="appt-cal-clear">Clear date</button>
                  <button className="btn btn-ink btn-sm" onClick={() => setCalOpen(false)}>Done</button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="appt-filter">
            <label>&nbsp;</label>
            <button className="btn btn-ghost" onClick={clearFilters} data-testid="appt-filter-clear">Clear filters</button>
          </div>
        </div>

        {searching && <p className="sub" data-testid="appt-search-note" style={{ marginTop: -6 }}>Showing search results for “{q}”. Clear the search to use the filters.</p>}

        <table data-testid="appointments-table">
          <thead><tr>
            <th><button type="button" className={`th-sort ${sort.key === "patient" ? "on" : ""}`} onClick={() => toggleSort("patient")} data-testid="appt-sort-patient">Patient <ArrowUpDown size={12} className="th-sort-ic" /></button></th>
            <th>Patient ID</th>
            <th><button type="button" className={`th-sort ${sort.key === "doctor" ? "on" : ""}`} onClick={() => toggleSort("doctor")} data-testid="appt-sort-doctor">Doctor <ArrowUpDown size={12} className="th-sort-ic" /></button></th>
            <th>NMC</th>
            <th><button type="button" className={`th-sort ${sort.key === "specialty" ? "on" : ""}`} onClick={() => toggleSort("specialty")} data-testid="appt-sort-specialty">Specialty <ArrowUpDown size={12} className="th-sort-ic" /></button></th>
            <th><button type="button" className={`th-sort ${sort.key === "date" ? "on" : ""}`} onClick={() => toggleSort("date")} data-testid="appt-sort-date">When <ArrowUpDown size={12} className="th-sort-ic" /></button></th>
            <th>Status</th>
          </tr></thead>
          <tbody>
            {view.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", padding: "26px 0", color: "var(--slate)" }}>No appointments match.</td></tr>}
            {view.map(a => (
              <tr key={a.id} data-testid={`appt-row-${a.id}`}>
                <td><b>{a.patient_name}</b></td>
                <td>{a.patient_code ? <span className="pid-chip">{a.patient_code}</span> : "—"}</td>
                <td>{a.doctor_name || "—"}</td>
                <td>{a.doctor_reg_no ? <span className="pid-chip">{a.doctor_reg_no}</span> : "—"}</td>
                <td>{a.specialty || "—"}</td>
                <td>{fmtApptDate(a.date)} <small style={{ color: "var(--slate)" }}>{a.time}</small></td>
                <td>
                  <div className="status-cell">
                    <span className={`pill-mini ${a.status}`} data-testid={`appt-status-${a.id}`}>{a.status}</span>
                    <select className="status-override" value="" onChange={e => { if (e.target.value) overrideStatus(a.id, e.target.value); }} title="Override status" data-testid={`appt-override-${a.id}`}>
                      <option value="">⋯</option>
                      <option value="upcoming">Set Upcoming</option>
                      <option value="completed">Set Completed</option>
                      <option value="pending">Set Pending (doctor missed)</option>
                      <option value="missed">Set Missed (patient missed)</option>
                      <option value="cancelled">Set Cancelled</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Content() {
  const [c, setC] = useState({});
  const [title, setTitle] = useState(""); const [file, setFile] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [aomVideo, setAomVideo] = useState(null);
  const load = () => api.get("/public/content").then(r => setC(r.data.content || {}));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const fields = [
    ["hero_headline", "Hero headline", "input"], ["hero_sub", "Hero subtitle", "textarea"],
    ["mission", "Mission statement", "textarea"], ["article_title", "Article of the month — title", "input"],
    ["article_excerpt", "Article excerpt", "textarea"],
    ["article_video_caption", "Article of the month — video summary text (English, used for voice-over & translation)", "textarea"],
    ["founder_name", "Founder name", "input"], ["founder_role", "Founder role", "input"], ["founder_blurb", "Founder blurb", "input"],
    ["cmo_name", "Chief Medical Officer — name", "input"], ["cmo_blurb", "CMO blurb", "input"],
    ["cto_name", "Technology lead — name", "input"], ["cto_blurb", "Technology blurb", "input"],
  ];
  const save = async () => {
    const payload = Object.fromEntries(fields.map(([k]) => [k, c[k] || ""]));
    try { await api.put("/admin/content", payload); toast("Content saved ✓"); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const uploadArticle = async () => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("title", title);
    try { await api.post("/admin/content/article", fd); toast("Article uploaded ✓"); load(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const uploadAomVideo = async () => {
    if (!aomVideo) return;
    if (aomVideo.size > 50 * 1024 * 1024) { toast("Video must be 50 MB or smaller."); return; }
    const fd = new FormData(); fd.append("file", aomVideo);
    try { await api.post("/admin/content/article-video", fd); toast("Summary video uploaded ✓"); setAomVideo(null); load(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const removeAomVideo = async () => {
    if (!window.confirm("Remove the Article-of-the-Month summary video?")) return;
    try { await api.delete("/admin/content/article-video"); toast("Video removed"); load(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const uploadPhoto = async () => {
    if (!photo) return;
    const fd = new FormData(); fd.append("file", photo); fd.append("slot", "founder");
    try { await api.post("/admin/content/image", fd); toast("Founder photo updated ✓"); setPhoto(null); load(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <>
      <div className="panel">
        <h3>Website content</h3><p className="sub">Update the public site copy</p>
        {fields.map(([k, l, t]) => (
          <div className="field" key={k}><label>{l}</label>
            {t === "textarea" ? <textarea className="in" value={c[k] || ""} onChange={set(k)} data-testid={`content-${k}`} />
              : <input className="in" value={c[k] || ""} onChange={set(k)} data-testid={`content-${k}`} />}
          </div>
        ))}
        <button className="btn btn-ink" onClick={save} data-testid="content-save">Save content</button>
      </div>
      <div className="panel">
        <h3>Founder photo</h3><p className="sub">Replace the leadership image shown on the About page</p>
        <label className="uploadbox"><input type="file" accept="image/*" hidden onChange={e => setPhoto(e.target.files[0])} /><Upload size={22} color="#185FA5" /><p>{photo ? photo.name : "Choose an image"}</p><small>JPG / PNG</small></label>
        <button className="btn btn-ink" style={{ marginTop: 12 }} onClick={uploadPhoto} data-testid="photo-upload">Update founder photo</button>
      </div>
      <div className="panel">
        <h3>Article of the month — upload</h3><p className="sub">Attach a PDF or Word document; its title becomes the Journal headline</p>
        <div className="field"><label>Title</label><input className="in" value={title} onChange={e => setTitle(e.target.value)} /></div>
        <label className="uploadbox"><input type="file" hidden onChange={e => setFile(e.target.files[0])} /><Upload size={22} color="#185FA5" /><p>{file ? file.name : "Choose a file"}</p><small>PDF / DOCX</small></label>
        <button className="btn btn-ink" style={{ marginTop: 12 }} onClick={uploadArticle} data-testid="article-upload">Upload article</button>

        <div style={{ marginTop: 18, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
          <h4 className="h" style={{ fontSize: 14, margin: "0 0 4px" }}>Summary video <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span></h4>
          <p className="sub" style={{ marginTop: 0 }}>A short video that summarises the article so patients grasp the key points quickly. Patients can also auto-translate the spoken summary into their language. MP4 / WebM / MOV · max 50 MB.</p>
          {c.article_video_id ? (
            <div className="sa-row" data-testid="aom-video-current">
              <div><b>Video attached</b><div className="sa-meta">{c.article_video_filename || "summary video"}</div></div>
              <button className="pid-mini" title="Remove video" onClick={removeAomVideo} data-testid="aom-video-remove"><Trash2 size={14} /></button>
            </div>
          ) : <p className="sub" style={{ fontStyle: "italic" }}>No summary video yet.</p>}
          <label className="uploadbox" style={{ marginTop: 10 }}><input type="file" accept="video/*" hidden onChange={e => setAomVideo(e.target.files[0])} data-testid="aom-video-file" /><Upload size={22} color="#185FA5" /><p>{aomVideo ? aomVideo.name : "Choose a video"}</p><small>MP4 / WebM / MOV · max 50 MB</small></label>
          <button className="btn btn-green" style={{ marginTop: 12 }} onClick={uploadAomVideo} disabled={!aomVideo} data-testid="aom-video-upload">{c.article_video_id ? "Replace video" : "Upload video"}</button>
        </div>
      </div>
      <SpecialtyArticles />
    </>
  );
}

function SpecialtyArticles() {
  const [meta, setMeta] = useState({ specialties: [], max_per_specialty: 10 });
  const [specialty, setSpecialty] = useState("");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = (spec) => api.get("/admin/articles", { params: spec ? { specialty: spec } : {} })
    .then(r => { setMeta(r.data); setItems(r.data.articles || []); if (!specialty && r.data.specialties?.length) setSpecialty(r.data.specialties[0]); })
    .catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (specialty) api.get("/admin/articles", { params: { specialty } }).then(r => setItems(r.data.articles || [])); }, [specialty]);
  const reload = async () => { const r = await api.get("/admin/articles", { params: { specialty } }); setItems(r.data.articles || []); };

  const upload = async () => {
    if (!specialty) { toast("Choose a specialty"); return; }
    if (!file) { toast("Choose a PDF file"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("specialty", specialty); fd.append("title", title); fd.append("caption", caption);
    try {
      await api.post("/admin/articles", fd);
      toast("Article published ✓ — patients can now read it on the site");
      setFile(null); setTitle(""); setCaption("");
      await reload();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/articles/${id}`); await reload(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const makeFree = async (id) => {
    try { await api.put(`/admin/articles/${id}`, { is_free: true }); toast("Set as the free article for this specialty ✓"); await reload(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const uploadVideo = async (id, f) => {
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast("Video must be 50 MB or smaller."); return; }
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/admin/articles/${id}/video`, fd); toast("Summary video uploaded ✓"); await reload(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const removeVideo = async (id) => {
    if (!window.confirm("Remove this article's summary video?")) return;
    try { await api.delete(`/admin/articles/${id}/video`); await reload(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const saveCaption = async (id, cap) => {
    try { await api.put(`/admin/articles/${id}`, { caption: cap }); toast("Video summary text saved ✓"); await reload(); } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const specItems = items.filter(a => a.specialty === specialty);

  return (
    <div className="panel" data-testid="specialty-articles">
      <h3>Specialty articles</h3>
      <p className="sub">Upload a PDF under a specialty — the system extracts the text so patients read it on-site (no download). Up to {meta.max_per_specialty} per specialty. Pick which article is <b>free</b> for all patients; the rest are Premium-only. Each article can carry an optional summary video that patients can auto-translate into their language.</p>
      <div className="field"><label>Specialty</label>
        <select className="in" value={specialty} onChange={e => setSpecialty(e.target.value)} data-testid="sa-specialty">
          {meta.specialties.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="field"><label>Title (optional — defaults to the file name)</label><input className="in" value={title} onChange={e => setTitle(e.target.value)} data-testid="sa-title" /></div>
      <div className="field"><label>Video summary text (optional — spoken summary, used for the video voice-over & translation)</label><textarea className="in" value={caption} onChange={e => setCaption(e.target.value)} data-testid="sa-caption" placeholder="A short, plain-language summary of the article in English." /></div>
      <label className="uploadbox"><input type="file" accept="application/pdf" hidden onChange={e => setFile(e.target.files[0])} data-testid="sa-file" /><Upload size={22} color="#185FA5" /><p>{file ? file.name : "Choose a PDF"}</p><small>Text-based PDF</small></label>
      <button className="btn btn-green" style={{ marginTop: 12 }} onClick={upload} disabled={busy} data-testid="sa-upload">{busy ? "Publishing…" : "Publish article"}</button>

      <div style={{ marginTop: 18 }}>
        <h4 className="h" style={{ fontSize: 14, margin: "0 0 8px" }}>{specialty} — {specItems.length}/{meta.max_per_specialty}</h4>
        {specItems.length === 0 && <p className="sub" style={{ fontStyle: "italic" }}>No articles yet for this specialty.</p>}
        {specItems.map((a) => (
          <ArticleAdminRow key={a.id} a={a} onFree={makeFree} onDelete={del}
            onUploadVideo={uploadVideo} onRemoveVideo={removeVideo} onSaveCaption={saveCaption} />
        ))}
      </div>
    </div>
  );
}

function ArticleAdminRow({ a, onFree, onDelete, onUploadVideo, onRemoveVideo, onSaveCaption }) {
  const [cap, setCap] = useState(a.caption || "");
  useEffect(() => { setCap(a.caption || ""); }, [a.caption]);
  return (
    <div className="sa-row" data-testid={`sa-item-${a.id}`} style={{ flexDirection: "column", alignItems: "stretch", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div>
          <b>{a.title}</b>
          <div className="sa-meta">
            {a.is_free ? "✓ Free for all patients" : "Premium only"} · {(a.body || "").length} chars
            {a.video_file_id ? " · 🎬 video attached" : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {!a.is_free && (
            <button className="btn btn-ghost btn-sm" onClick={() => onFree(a.id)} data-testid={`sa-free-${a.id}`}>Make free</button>
          )}
          <button className="pid-mini" title="Delete" onClick={() => onDelete(a.id)} data-testid={`sa-del-${a.id}`}><Trash2 size={14} /></button>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
          <input type="file" accept="video/*" hidden onChange={e => { onUploadVideo(a.id, e.target.files[0]); e.target.value = ""; }} data-testid={`sa-video-file-${a.id}`} />
          <Upload size={13} /> {a.video_file_id ? "Replace summary video" : "Add summary video"}
        </label>
        {a.video_file_id && (
          <button className="btn btn-ghost btn-sm" onClick={() => onRemoveVideo(a.id)} data-testid={`sa-video-remove-${a.id}`}><Trash2 size={13} /> Remove video</button>
        )}
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label style={{ fontSize: 12 }}>Video summary text (used for voice-over & translation)</label>
        <textarea className="in" rows={2} value={cap} onChange={e => setCap(e.target.value)} data-testid={`sa-caption-${a.id}`} placeholder="A short, plain-language summary in English." />
        <button className="btn btn-ink btn-sm" style={{ marginTop: 6, alignSelf: "flex-start" }} onClick={() => onSaveCaption(a.id, cap)} data-testid={`sa-caption-save-${a.id}`}>Save summary text</button>
      </div>
    </div>
  );
}

function Specialties() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", diseases: "" });
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/public/specialties").then(r => setRows(r.data.specialties || []));
  useEffect(() => { load(); }, []);
  const toLines = (v) => (v || []).join("\n");
  const fromLines = (v) => (v || "").split("\n").map(s => s.trim()).filter(Boolean);
  const add = async () => {
    try { await api.post("/admin/specialties", { name: form.name, diseases: form.diseases.split(",").map(s => s.trim()).filter(Boolean) }); toast("Specialty added ✓"); setForm({ name: "", diseases: "" }); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (s) => setEdit({
    id: s.id, name: s.name, diseases: (s.diseases || []).join(", "),
    about: s.about || "", symptoms: toLines(s.symptoms), investigations: toLines(s.investigations),
    treatments: toLines(s.treatments), photos: s.photos || [],
  });
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/specialties/${edit.id}`, {
        name: edit.name, diseases: edit.diseases.split(",").map(s => s.trim()).filter(Boolean),
        about: edit.about, symptoms: fromLines(edit.symptoms),
        investigations: fromLines(edit.investigations), treatments: fromLines(edit.treatments),
      });
      toast("Specialty saved ✓"); setEdit(null); load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const uploadPhoto = async (slot, fileEl) => {
    const file = fileEl.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("slot", slot);
    try { const { data } = await api.post(`/admin/specialties/${edit.id}/photo`, fd);
      const next = [...(edit.photos || [])]; while (next.length < 4) next.push(""); next[data.slot] = data.file_id;
      setEdit({ ...edit, photos: next }); toast(`Photo ${slot + 1} updated ✓`); load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const photoSrc = (p) => p && p.startsWith("http") ? p : `${API}/public/image/${p}`;
  const del = async (id) => { await api.delete(`/admin/specialties/${id}`); toast("Deleted"); load(); };
  const move = async (id, direction) => { await api.post("/admin/specialties/reorder", { spec_id: id, direction }); load(); };
  return (
    <>
      <div className="panel">
        <h3>Add specialty</h3>
        <div className="field"><label>Name</label><input className="in" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="spec-name" /></div>
        <div className="field"><label>Conditions (comma separated)</label><input className="in" value={form.diseases} onChange={e => setForm({ ...form, diseases: e.target.value })} data-testid="spec-diseases" /></div>
        <button className="btn btn-ink" onClick={add} data-testid="spec-add"><Plus size={15} /> Add specialty</button>
      </div>

      {edit && (
        <div className="panel" data-testid="spec-editor">
          <h3>Edit specialty page — {edit.name}</h3><p className="sub">This is the public specialty page patients open from Services → Specialties.</p>
          <div className="row2">
            <div className="field"><label>Name</label><input className="in" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} data-testid="spec-edit-name" /></div>
            <div className="field"><label>Conditions (comma separated)</label><input className="in" value={edit.diseases} onChange={e => setEdit({ ...edit, diseases: e.target.value })} data-testid="spec-edit-diseases" /></div>
          </div>
          <div className="field"><label>About — what this specialty is</label><textarea className="in" rows={3} value={edit.about} onChange={e => setEdit({ ...edit, about: e.target.value })} data-testid="spec-edit-about" /></div>
          <div className="row2">
            <div className="field"><label>When to seek it / symptoms <span className="opt">(one per line)</span></label><textarea className="in" rows={5} value={edit.symptoms} onChange={e => setEdit({ ...edit, symptoms: e.target.value })} data-testid="spec-edit-symptoms" /></div>
            <div className="field"><label>Investigations <span className="opt">(one per line)</span></label><textarea className="in" rows={5} value={edit.investigations} onChange={e => setEdit({ ...edit, investigations: e.target.value })} data-testid="spec-edit-investigations" /></div>
          </div>
          <div className="field"><label>Treatments <span className="opt">(one per line)</span></label><textarea className="in" rows={4} value={edit.treatments} onChange={e => setEdit({ ...edit, treatments: e.target.value })} data-testid="spec-edit-treatments" /></div>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Photo collage (4 images)</label>
          <div className="spec-photo-grid">
            {[0, 1, 2, 3].map(slot => {
              const p = (edit.photos || [])[slot];
              return (
                <div className="spec-photo-slot" key={slot}>
                  <div className="spec-photo-prev">{p ? <img src={photoSrc(p)} alt="" onError={(e) => { e.target.style.display = "none"; }} /> : <Camera size={20} />}</div>
                  <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer", marginTop: 6 }}>
                    <input type="file" accept="image/*" hidden onChange={(e) => uploadPhoto(slot, e.target)} data-testid={`spec-photo-${slot}`} />
                    <Upload size={12} /> Photo {slot + 1}
                  </label>
                </div>
              );
            })}
          </div>
          <div className="about-footer" style={{ marginTop: 16 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEdit(null)}><X size={13} /> Close</button>
            <button className="btn btn-ink" onClick={saveEdit} disabled={busy} data-testid="spec-edit-save">{busy ? "Saving…" : "Save specialty page"}</button>
          </div>
        </div>
      )}

      <div className="panel">
        <h3>Specialties</h3><p className="sub">Reorder, edit the full page, or remove</p>
        {rows.map((s, i) => (
          <div className="listrow" key={s.id} data-testid={`spec-row-${s.name}`}>
            <div className="grab">{i + 1}</div>
            <div className="m"><h4>{s.name}</h4><p>{(s.diseases || []).join(", ")}</p></div>
            <div className="acts">
              <button className="iconbtn" onClick={() => move(s.id, -1)}><ArrowUp size={15} /></button>
              <button className="iconbtn" onClick={() => move(s.id, 1)}><ArrowDown size={15} /></button>
              <button className="iconbtn" onClick={() => startEdit(s)} data-testid={`spec-edit-${s.name}`}><Pencil size={15} /></button>
              <button className="iconbtn del" onClick={() => del(s.id)}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Header() {
  const [nav, setNav] = useState([]); const [svc, setSvc] = useState([]);
  useEffect(() => { api.get("/public/nav").then(r => { setNav(r.data.nav || []); setSvc(r.data.services || []); }); }, []);
  const saveNav = async () => { await api.put("/admin/nav", { items: nav }); toast("Header saved ✓"); };
  const saveSvc = async () => { await api.put("/admin/services", { items: svc }); toast("Services saved ✓"); };
  return (
    <>
      <div className="panel">
        <h3>Header navigation</h3><p className="sub">Edit menu labels</p>
        {nav.map((it, i) => (
          <div className="row2" key={i} style={{ marginBottom: 8 }}>
            <input className="in" value={it.label} onChange={e => { const n = [...nav]; n[i] = { ...n[i], label: e.target.value }; setNav(n); }} />
            <input className="in" value={it.link} onChange={e => { const n = [...nav]; n[i] = { ...n[i], link: e.target.value }; setNav(n); }} />
          </div>
        ))}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setNav([...nav, { label: "New", link: "home" }])}><Plus size={13} /> Add</button>
          <button className="btn btn-ink btn-sm" onClick={saveNav} data-testid="nav-save">Save header</button>
        </div>
      </div>
      <div className="panel">
        <h3>Services menu</h3>
        {svc.map((s, i) => <div key={i} style={{ marginBottom: 8 }}><input className="in" value={s} onChange={e => { const n = [...svc]; n[i] = e.target.value; setSvc(n); }} /></div>)}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setSvc([...svc, "New service"])}><Plus size={13} /> Add</button>
          <button className="btn btn-ink btn-sm" onClick={saveSvc}>Save services</button>
        </div>
      </div>
    </>
  );
}

function Comms() {
  const [form, setForm] = useState({ audience: "All patients", channel: "Email", subject: "", body: "", recipient_email: "" });
  const send = async () => {
    try {
      const payload = { ...form };
      if (!payload.recipient_email) delete payload.recipient_email;
      const { data } = await api.post("/admin/messages", payload);
      toast(`Sent to ${data.recipients} recipient(s) ✓`); setForm({ ...form, subject: "", body: "", recipient_email: "" });
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="panel">
      <h3>Communications</h3><p className="sub">Message all patients/doctors, or an individual, via email, SMS or WhatsApp</p>
      <div className="row2">
        <div className="field"><label>Audience</label>
          <select className="in" value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })} data-testid="comms-audience">
            <option>All patients</option><option>All doctors</option><option>All patients and doctors</option><option>Customer Support</option>
          </select></div>
        <div className="field"><label>Channel</label>
          <select className="in" value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} data-testid="comms-channel">
            <option>Email</option><option>SMS</option><option>WhatsApp</option>
          </select></div>
      </div>
      <div className="field"><label>Or send to one person (email — optional)</label>
        <input className="in" placeholder="patient or doctor email" value={form.recipient_email} onChange={e => setForm({ ...form, recipient_email: e.target.value })} data-testid="comms-individual" /></div>
      <div className="field"><label>Subject</label><input className="in" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} /></div>
      <div className="field"><label>Message</label><textarea className="in" rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} data-testid="comms-body" /></div>
      <button className="btn btn-ink" onClick={send} data-testid="comms-send"><Megaphone size={15} /> Send message</button>
    </div>
  );
}

function CommsSection() {
  return (
    <>
      <Comms />
      <AdminTicketInbox />
    </>
  );
}

function Callbacks() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/callbacks").then(r => setRows(r.data.callbacks || [])); }, []);
  return (
    <div className="panel">
      <h3>Callback requests</h3><p className="sub">{rows.length} total</p>
      <table><thead><tr><th>Name</th><th>Phone</th><th>Service</th><th>Preferred</th><th>Note</th><th>When</th></tr></thead>
        <tbody>{rows.map(c => <tr key={c.id}><td>{c.name}</td><td>{c.phone}</td><td>{c.service || "—"}</td><td>{(c.preferred_datetime || "—").replace("T", " ")}</td><td>{c.note}</td><td>{(c.created_at || "").slice(0, 16).replace("T", " ")}</td></tr>)}</tbody></table>
    </div>
  );
}

function AboutUs() {
  const [members, setMembers] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", blurb: "", tagline: "", linkedin: "", email: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const load = async () => { const r = await api.get("/admin/team"); const list = r.data.team || []; setMembers(list); return list; };
  useEffect(() => { load(); }, []);
  const select = (m) => { setSel(m.id); setForm({ name: m.name || "", role: m.role || "", blurb: m.blurb || "", tagline: m.tagline || "", linkedin: m.linkedin || "", email: m.email || "" }); setPhoto(null); };
  const addRole = async () => {
    try { const { data } = await api.post("/admin/team", { role: "New role", name: "", blurb: "" }); await load(); select(data.member); toast("Role added — fill in the details"); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const removeRole = async (id) => {
    if (!window.confirm("Remove this role from the public About page?")) return;
    try { await api.delete(`/admin/team/${id}`); if (sel === id) setSel(null); await load(); toast("Role removed"); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const onDrop = async (targetIdx) => {
    const from = dragIdx; setDragIdx(null); setOverIdx(null);
    if (from === null || from === targetIdx) return;
    const next = [...members];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    setMembers(next);
    try { await api.post("/admin/team/order", { ids: next.map(m => m.id) }); toast("Order updated ✓"); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); load(); }
  };
  const save = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await api.put(`/admin/team/${sel}`, form);
      if (photo) { const fd = new FormData(); fd.append("file", photo); await api.post(`/admin/team/${sel}/photo`, fd); setPhoto(null); }
      toast("Saved — the About page is updated ✓"); await load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const current = members.find(m => m.id === sel);
  return (
    <div className="about-cms">
      <div className="panel">
        <h3>Team &amp; leadership</h3><p className="sub">Shown on the public About page. Drag the handle to reorder; click a role to edit.</p>
        {members.map((m, i) => (
          <div
            className={`listrow team-row ${sel === m.id ? "on" : ""} ${overIdx === i ? "drop-over" : ""} ${dragIdx === i ? "dragging" : ""}`}
            key={m.id} data-testid={`team-row-${m.id}`} onClick={() => select(m)}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
            onDrop={() => onDrop(i)}
          >
            <span className="drag-handle" title="Drag to reorder" onClick={(e) => e.stopPropagation()}><GripVertical size={16} /></span>
            <div className="team-ava">{m.photo_id ? <img src={`${API}/public/image/${m.photo_id}`} alt="" /> : <Contact size={18} />}</div>
            <div className="m"><h4>{m.role || "Untitled role"}</h4><p>{m.name || "No name yet"}</p></div>
            <div className="acts" onClick={(e) => e.stopPropagation()}>
              <button className="iconbtn del" onClick={() => removeRole(m.id)} title="Remove role"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {members.length === 0 && <p className="sub">No roles yet — add the first one.</p>}
        <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={addRole} data-testid="team-add"><Plus size={15} /> Add role</button>
      </div>

      {current ? (
        <div className="panel about-editor" data-testid="team-editor">
          <h3>Edit profile</h3><p className="sub">Changes go live on the public About page when you save.</p>
          <div className="about-photo">
            <div className="about-photo-prev">
              {photo ? <img src={URL.createObjectURL(photo)} alt="" />
                : current.photo_id ? <img src={`${API}/public/image/${current.photo_id}`} alt="" />
                  : <Camera size={26} />}
            </div>
            <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
              <input type="file" accept="image/*" hidden onChange={(e) => setPhoto(e.target.files[0])} data-testid="team-photo-input" />
              <Upload size={13} /> {photo ? photo.name : "Choose photo"}
            </label>
          </div>
          <div className="field"><label>Role title</label><input className="in" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Co-founder & COO" data-testid="team-role" /></div>
          <div className="field"><label>Name</label><input className="in" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" /></div>
          <div className="field"><label>Tagline <span className="opt">(optional, one line)</span></label><input className="in" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="e.g. 18 years in oncology" data-testid="team-tagline" /></div>
          <div className="field"><label>Profile</label><textarea className="in" rows={5} value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} placeholder="A short professional bio shown under the name." data-testid="team-blurb" /></div>
          <div className="row2">
            <div className="field"><label>LinkedIn URL <span className="opt">(optional)</span></label><input className="in" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" data-testid="team-linkedin" /></div>
            <div className="field"><label>Email <span className="opt">(optional)</span></label><input className="in" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@2ndopinionconsult.in" data-testid="team-email" /></div>
          </div>
          <div className="about-footer">
            <button className="btn btn-danger btn-sm" onClick={() => removeRole(sel)} data-testid="team-remove"><Trash2 size={13} /> Remove role</button>
            <button className="btn btn-ink" onClick={save} disabled={busy} data-testid="team-save">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </div>
      ) : (
        <div className="panel about-editor"><p className="sub" style={{ textAlign: "center", padding: "44px 0" }}>Select a role on the left to edit its profile, or add a new one.</p></div>
      )}
    </div>
  );
}

const ACTION_LABEL = {
  "patient.create": "Patient created",
  "patient.update": "Patient edited",
  "patient.delete": "Patient deleted",
  "patient.contact_change": "Contact changed",
};

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/audit-logs").then(r => setLogs(r.data.logs || [])); }, []);
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/audit-logs/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "audit-log.csv"; a.click();
    } catch { toast("Could not export audit log"); }
  };
  const filtered = logs.filter(l => {
    if (!q.trim()) return true;
    const s = `${l.patient_code} ${l.actor_name} ${l.actor_email} ${l.summary} ${ACTION_LABEL[l.action] || l.action}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });
  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <h3><ShieldAlert size={18} style={{ verticalAlign: "-3px" }} /> Audit log</h3>
          <p className="sub">Immutable trail of personal-data actions — DPDP Act, 2023 accountability. {logs.length} entries.</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input className="in" style={{ maxWidth: 280 }} placeholder="Search ID, admin, action…" value={q} onChange={e => setQ(e.target.value)} data-testid="audit-search" />
          <button className="btn btn-ink btn-sm" onClick={exportCsv} data-testid="audit-export-btn"><Download size={14} /> Export CSV</button>
        </div>
      </div>
      <table data-testid="audit-table">
        <thead><tr><th>When</th><th>Action</th><th>Patient ID</th><th>By</th><th>Details</th><th>IP</th></tr></thead>
        <tbody>
          {filtered.length === 0 && <tr><td colSpan={6}><span className="sub">No audit entries yet.</span></td></tr>}
          {filtered.map(l => (
            <tr key={l.id} data-testid={`audit-row-${l.id}`}>
              <td style={{ whiteSpace: "nowrap" }}>{new Date(l.created_at).toLocaleString()}</td>
              <td><span className={`audit-tag ${l.action === "patient.delete" ? "danger" : l.action === "patient.create" ? "ok" : ""}`}>{ACTION_LABEL[l.action] || l.action}</span></td>
              <td><span className="pid-chip">{l.patient_code || "—"}</span></td>
              <td>{l.actor_name || "—"}<br /><span className="sub" style={{ fontSize: 11 }}>{l.actor_role}</span></td>
              <td style={{ maxWidth: 320 }}>{l.summary}</td>
              <td><span className="sub" style={{ fontSize: 11 }}>{l.ip || "—"}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClinicalAuditLog() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/admin/clinical-audit-logs?limit=1000")
    .then(r => setLogs(r.data.logs || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/clinical-audit-logs/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "clinical-audit-logs.csv"; a.click();
    } catch { toast("Could not export clinical audit log"); }
  };
  const tone = (a) => a === "RECORD_DELETION_REQUESTED" ? "danger"
    : a === "RECORD_UPLOADED" ? "ok" : "";
  const label = (a) => ({
    RECORD_UPLOADED: "Uploaded", SIGNED_URL_GENERATED: "Secure link issued",
    RECORD_VIEWED: "Viewed", RECORD_DOWNLOADED: "Downloaded",
    RECORD_DELETION_REQUESTED: "Deletion requested",
  }[a] || a);
  const filtered = logs.filter(l => {
    if (!q.trim()) return true;
    const s = `${l.patient_id} ${l.file_id} ${l.actor_id} ${l.actor_role} ${l.action_type} ${label(l.action_type)} ${l.ip_address}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });
  return (
    <div className="panel" data-testid="clinical-audit-panel">
      <div className="panel-head">
        <div>
          <h3><ShieldAlert size={18} style={{ verticalAlign: "-3px" }} /> Clinical audit logs</h3>
          <p className="sub">Append-only, immutable trail of every medical-file access — upload, secure-link issue, view, download and deletion request. HIPAA / DPDP 2023 / GDPR accountability. {logs.length} entries.</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
          <input className="in" style={{ width: 300, maxWidth: "40vw" }} placeholder="Search patient, file, actor, action, IP…" value={q} onChange={e => setQ(e.target.value)} data-testid="clinical-audit-search" />
          <button className="btn btn-ink btn-sm" style={{ whiteSpace: "nowrap" }} onClick={exportCsv} data-testid="clinical-audit-export-btn"><Download size={14} /> Export CSV</button>
        </div>
      </div>
      <table data-testid="clinical-audit-table">
        <thead><tr><th>When (UTC)</th><th>Action</th><th>Patient ID</th><th>File ID</th><th>Actor</th><th>Role</th><th>IP</th></tr></thead>
        <tbody>
          {loading && <tr><td colSpan={7}><span className="sub">Loading…</span></td></tr>}
          {!loading && filtered.length === 0 && <tr><td colSpan={7}><span className="sub">No clinical file access recorded yet.</span></td></tr>}
          {filtered.map(l => (
            <tr key={l.id} data-testid={`clinical-audit-row-${l.id}`}>
              <td style={{ whiteSpace: "nowrap", fontSize: 12.5 }}>{new Date(l.timestamp).toLocaleString()}</td>
              <td><span className={`audit-tag ${tone(l.action_type)}`}>{label(l.action_type)}</span></td>
              <td><span className="pid-chip">{l.patient_id || "—"}</span></td>
              <td><span className="sub" style={{ fontSize: 11, fontFamily: "monospace" }} title={l.file_id || ""}>{(l.file_id || "—").slice(0, 12)}</span></td>
              <td><span className="sub" style={{ fontSize: 11, fontFamily: "monospace" }} title={l.actor_id || ""}>{(l.actor_id || "—").slice(0, 12)}</span></td>
              <td><span className="sub" style={{ fontSize: 11 }}>{l.actor_role || "—"}</span></td>
              <td><span className="sub" style={{ fontSize: 11 }}>{l.ip_address || "—"}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SupportEditModal({ emp, onClose, onSaved }) {
  const [f, setF] = useState({
    name: emp.name || "", age: emp.age || "", sex: emp.sex || "",
    qualification: emp.qualification || "", mobile: emp.mobile || "",
    email: emp.email || "", employee_no: emp.employee_no || "",
  });
  const [languages, setLanguages] = useState(emp.languages || []);
  const [langInput, setLangInput] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const addLang = () => {
    const v = langInput.trim();
    if (v && !languages.some((l) => l.toLowerCase() === v.toLowerCase())) setLanguages([...languages, v]);
    setLangInput("");
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/support/${emp.id}`, { ...f, languages });
      toast("Employee updated ✓");
      onSaved(data.employee);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="support-edit-modal">
        <button className="m-close" onClick={onClose}>✕</button>
        <h3 className="h" style={{ fontSize: 20, marginTop: 0 }}>Edit employee</h3>
        <div className="field"><label>Employee number</label><input className="in" value={f.employee_no} onChange={set("employee_no")} data-testid="support-edit-empno" /></div>
        <div className="field"><label>Name (as per Aadhaar)</label><input className="in" value={f.name} onChange={set("name")} data-testid="support-edit-name" /></div>
        <div className="field two">
          <div><label>Age</label><input className="in" value={f.age} onChange={set("age")} /></div>
          <div><label>Gender</label>
            <select className="in" value={f.sex} onChange={set("sex")}>
              <option value="">Select</option>{["Male", "Female", "Other", "Prefer not to say"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="field"><label>Qualification</label><input className="in" value={f.qualification} onChange={set("qualification")} /></div>
        <div className="field"><label>Languages known</label>
          <div className="lang-add">
            <input className="in" value={langInput} onChange={(e) => setLangInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLang())} placeholder="Add a language" />
            <button type="button" className="btn btn-ghost btn-sm" onClick={addLang}><Plus size={14} /> Add</button>
          </div>
          {languages.length > 0 && <div className="lang-chips">{languages.map((l) => (
            <span className="lang-chip" key={l}>{l}<button type="button" onClick={() => setLanguages(languages.filter((x) => x !== l))}><X size={12} /></button></span>
          ))}</div>}
        </div>
        <div className="field two">
          <div><label>Email</label><input className="in" value={f.email} onChange={set("email")} /></div>
          <div><label>Phone</label><input className="in" value={f.mobile} onChange={set("mobile")} /></div>
        </div>
        <button className="btn btn-green full" onClick={save} disabled={busy} data-testid="support-edit-save">{busy ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}

function CustomerSupport() {
  const [tab, setTab] = useState("employees"); // employees | pending
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [auditing, setAuditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = tab === "pending" ? "/admin/support/pending" : "/admin/support";
      const { data } = await api.get(url);
      setRows(tab === "pending" ? data.pending : data.employees);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (emp, action) => {
    try {
      await api.patch(`/admin/support/${emp.id}/approval`, { action });
      toast(action === "approve" ? `${emp.employee_no} approved ✓` : `${emp.employee_no} rejected`);
      load();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const removeEmp = async (emp) => {
    if (!window.confirm(`Delete employee ${emp.employee_no} (${emp.name})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/support/${emp.id}`); toast("Employee deleted"); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  const ql = q.trim().toLowerCase();
  const filtered = ql ? rows.filter((r) => [r.name, r.email, r.mobile, r.employee_no, r.qualification, (r.languages || []).join(" ")].join(" ").toLowerCase().includes(ql)) : rows;
  const statusPill = (s) => <span className={`supp-status ${s}`}>{s === "approved" ? "Approved" : s === "rejected" ? "Rejected" : "Pending"}</span>;

  return (
    <div className="panel" data-testid="customer-support-section">
      <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Headset size={18} /> Customer Support</h3>
      <p className="sub" style={{ margin: "2px 0 16px" }}>Manage customer support employees, approvals and details.</p>

      <div className="supp-tabs">
        <button className={`supp-tab ${tab === "employees" ? "on" : ""}`} onClick={() => setTab("employees")} data-testid="support-tab-employees">Employees</button>
        <button className={`supp-tab pending ${tab === "pending" ? "on" : ""}`} onClick={() => setTab("pending")} data-testid="support-tab-pending">
          Pending Employee Approvals
        </button>
      </div>

      <div className="patient-search-bar" style={{ maxWidth: 560, marginBottom: 14 }}>
        <Search size={15} className="patient-search-ic" />
        <input className="patient-search-input" placeholder="Search by name, email, phone, employee no…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="support-search" />
        {q && <button className="patient-search-clear" onClick={() => setQ("")}>✕</button>}
      </div>

      <table data-testid="support-table">
        <thead><tr>
          <th>Employee No.</th><th>Name</th><th>Age/Sex</th><th>Qualification</th><th>Languages</th>
          <th>Email</th><th>Phone</th><th>Docs</th><th>Status</th><th>Actions</th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={10} style={{ textAlign: "center", padding: 24, color: "var(--slate)" }}>Loading…</td></tr>}
          {!loading && filtered.length === 0 && (
            <tr><td colSpan={10} style={{ textAlign: "center", padding: 28, color: "var(--slate)" }} data-testid="support-empty">
              {tab === "pending" ? "No employees awaiting approval." : (ql ? `No employees match “${q}”.` : "No customer support employees yet.")}
            </td></tr>
          )}
          {!loading && filtered.map((r) => (
            <tr key={r.id} data-testid={`support-row-${r.id}`}>
              <td><span className="pid-chip" data-testid={`support-empno-${r.id}`}>{r.employee_no}</span></td>
              <td>{r.name}</td>
              <td>{[r.age, r.sex].filter(Boolean).join(" / ") || "—"}</td>
              <td>{r.qualification || "—"}</td>
              <td>{(r.languages || []).join(", ") || "—"}</td>
              <td>{r.email}</td>
              <td>{r.mobile || "—"}</td>
              <td>
                {(r.documents || []).length === 0 ? "—" : (r.documents || []).map((d) => (
                  <a key={d.file_id} className="supp-doc" href={`${API}/files/${d.file_id}`} target="_blank" rel="noreferrer" title={d.filename}>
                    <FileText size={13} /> {d.type === "identity" ? "ID" : d.type === "qualification_cert" ? "Qual." : "Good standing"}
                  </a>
                ))}
              </td>
              <td>{statusPill(r.approval_status)}</td>
              <td>
                <span className="row-actions">
                  {r.approval_status === "approved" && <button className="pid-mini" title="View activity audit" onClick={() => setAuditing(r)} data-testid={`support-audit-${r.id}`}><BarChart3 size={13} /></button>}
                  {r.approval_status !== "approved" && <button className="pid-mini ok" title="Approve" onClick={() => decide(r, "approve")} data-testid={`support-approve-${r.id}`}><Check size={13} /></button>}
                  {r.approval_status === "pending" && <button className="pid-mini danger" title="Reject" onClick={() => decide(r, "reject")} data-testid={`support-reject-${r.id}`}><X size={13} /></button>}
                  <button className="pid-mini" title="Edit" onClick={() => setEditing(r)} data-testid={`support-edit-${r.id}`}><Pencil size={13} /></button>
                  <button className="pid-mini danger" title="Delete" onClick={() => removeEmp(r)} data-testid={`support-delete-${r.id}`}><Trash2 size={13} /></button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && <SupportEditModal emp={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {auditing && <SupportAuditModal emp={auditing} onClose={() => setAuditing(null)} />}
    </div>
  );
}


/* ----------------------- Support employee audit modal -------------------- */
function SupportAuditModal({ emp, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("patients");
  const [convo, setConvo] = useState(null); // {id, name} open conversation
  const [msgs, setMsgs] = useState([]);

  useEffect(() => {
    api.get(`/admin/support/${emp.id}/audit`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [emp.id]);

  const openConvo = async (c) => {
    setConvo(c);
    try { const { data: d } = await api.get(`/conversations/${c.id}/messages`); setMsgs(d.messages || []); }
    catch { setMsgs([]); }
  };

  const counts = data?.counts || {};
  const CARDS = [
    { key: "patients", label: "Patients registered", n: counts.patients_registered, icon: Users },
    { key: "appointments", label: "Appointments booked", n: counts.appointments_booked, icon: CalendarDays },
    { key: "doctors", label: "Doctors registered", n: counts.doctors_registered, icon: Stethoscope },
    { key: "callbacks", label: "Callbacks attended", n: counts.callbacks_attended, icon: Phone },
    { key: "conversations", label: "Conversations", n: counts.conversations, icon: MessageSquare },
  ];

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal audit-modal" onClick={(e) => e.stopPropagation()} data-testid="support-audit-modal">
        <button className="m-close" onClick={onClose}>✕</button>
        <h3 className="h" style={{ fontSize: 20, marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <BarChart3 size={18} /> Activity audit · {emp.name}
        </h3>
        <p className="sub" style={{ marginTop: 2 }}>Employee {emp.employee_no} · {emp.email}</p>

        {!data ? <p className="lead">Loading…</p> : data.error ? <p className="err">Could not load audit.</p> : (
          <>
            <div className="audit-cards" data-testid="audit-cards">
              {CARDS.map((c) => {
                const Icon = c.icon;
                return (
                  <button key={c.key} className={`audit-card ${tab === c.key ? "on" : ""}`} onClick={() => { setTab(c.key); setConvo(null); }} data-testid={`audit-card-${c.key}`}>
                    <Icon size={18} /><span className="audit-n" data-testid={`audit-count-${c.key}`}>{c.n ?? 0}</span><small>{c.label}</small>
                  </button>
                );
              })}
            </div>

            <div className="audit-body" data-testid="audit-body">
              {tab === "patients" && (data.patients.length === 0 ? <p className="lead">No patients registered.</p> :
                <table className="audit-table"><thead><tr><th>Name</th><th>Patient ID</th><th>Email</th><th>Status</th></tr></thead><tbody>
                  {data.patients.map((p) => <tr key={p.id}><td>{p.name}</td><td><span className="pid-chip">{p.patient_code || "—"}</span></td><td>{p.email}</td><td>{p.status}</td></tr>)}
                </tbody></table>)}
              {tab === "appointments" && (data.appointments.length === 0 ? <p className="lead">No appointments booked.</p> :
                <table className="audit-table"><thead><tr><th>Patient</th><th>Doctor</th><th>Specialty</th><th>When</th><th>Status</th></tr></thead><tbody>
                  {data.appointments.map((a) => <tr key={a.id}><td>{a.patient_name}</td><td>{a.doctor_name}</td><td>{a.specialty}</td><td>{a.date} {a.time}</td><td>{a.status}</td></tr>)}
                </tbody></table>)}
              {tab === "doctors" && (data.doctors.length === 0 ? <p className="lead">No doctors registered.</p> :
                <table className="audit-table"><thead><tr><th>Name</th><th>Specialty</th><th>Reg. no.</th><th>Status</th></tr></thead><tbody>
                  {data.doctors.map((d) => <tr key={d.id}><td>{d.name}</td><td>{d.specialty}</td><td>{d.reg_no}</td><td>{d.approval_status}</td></tr>)}
                </tbody></table>)}
              {tab === "callbacks" && (data.callbacks.length === 0 ? <p className="lead">No callbacks attended.</p> :
                <table className="audit-table"><thead><tr><th>Name</th><th>Phone</th><th>Service</th><th>Attended at</th></tr></thead><tbody>
                  {data.callbacks.map((c) => <tr key={c.id}><td>{c.name}</td><td>{c.phone}</td><td>{c.service || "—"}</td><td>{(c.attended_at || "").slice(0, 16).replace("T", " ")}</td></tr>)}
                </tbody></table>)}
              {tab === "conversations" && (convo ? (
                <div data-testid="audit-convo-thread">
                  <button className="compose-backlink" onClick={() => setConvo(null)}><ArrowLeft size={14} /> Back to conversations</button>
                  <h4 style={{ margin: "10px 0" }}>Conversation with {convo.name}</h4>
                  <div className="audit-thread">
                    {msgs.length === 0 ? <p className="lead">No messages.</p> : msgs.map((m) => (
                      <div className={`ib-bubble ${m.sender_role === "support" ? "me" : "them"}`} key={m.id}>
                        <span className="ib-who">{m.sender_name} · {m.sender_role}</span>
                        {m.text && <span className="ib-text">{m.text}</span>}
                        <span className="ib-time">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (data.conversations.length === 0 ? <p className="lead">No conversations handled.</p> :
                <div className="audit-convo-list">
                  {data.conversations.map((c) => (
                    <button key={c.id} className="audit-convo-row" onClick={() => openConvo({ id: c.id, name: c.with })} data-testid={`audit-convo-${c.id}`}>
                      <span className="ib-avatar admin"><MessageSquare size={15} /></span>
                      <span className="audit-convo-main">
                        <b>{c.with} {c.with_id && <span className="pid-chip">{c.with_id}</span>}</b>
                        <small>{c.ticket_code ? `${c.ticket_code} · ` : ""}{c.subject || (c.kind === "admin_support" ? "Internal chat with admin" : "")}{c.status ? ` · ${c.status}` : ""}</small>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


/* ------------------------------ Reviews (admin-only) ------------------------------ */
function AdminReviews() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get("/admin/reviews").then(r => setData(r.data)).catch(() => setData({ reviews: [], summary: [], count: 0, overall_avg: 0 })); }, []);
  if (!data) return <p className="lead">Loading…</p>;
  const reviews = filter ? data.reviews.filter(r => r.doctor_id === filter) : data.reviews;
  const Stars = ({ n }) => <span className="rv-stars">{[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} fill={i <= n ? "#f5a623" : "none"} color={i <= n ? "#f5a623" : "#cbd5e1"} />)}</span>;
  return (
    <div data-testid="admin-reviews">
      <div className="adm-kpi-row">
        <div className="adm-kpi"><span>Total reviews</span><b>{data.count}</b></div>
        <div className="adm-kpi"><span>Overall average</span><b>{data.overall_avg} <Star size={15} fill="#f5a623" color="#f5a623" style={{ verticalAlign: "-2px" }} /></b></div>
        <div className="adm-kpi"><span>Doctors rated</span><b>{data.summary.length}</b></div>
      </div>
      <p className="lead" style={{ fontSize: 13, margin: "6px 0 14px" }}>Reviews are visible to administrators only. Patients and doctors never see scores.</p>
      <div className="rv-grid">
        <div className="card">
          <h4 className="h" style={{ fontSize: 15, marginTop: 0 }}>By doctor</h4>
          <button className={`rv-doc ${!filter ? "on" : ""}`} onClick={() => setFilter("")} data-testid="rv-filter-all">All doctors</button>
          {data.summary.map(s => (
            <button key={s.doctor_id} className={`rv-doc ${filter === s.doctor_id ? "on" : ""}`} onClick={() => setFilter(s.doctor_id)} data-testid={`rv-filter-${s.doctor_id}`}>
              <span>{s.doctor_name}</span><b>{s.avg} ★ <small>({s.count})</small></b>
            </button>
          ))}
        </div>
        <div className="card">
          <h4 className="h" style={{ fontSize: 15, marginTop: 0 }}>Feedback ({reviews.length})</h4>
          {reviews.length === 0 && <p className="lead">No reviews yet.</p>}
          {reviews.map(r => (
            <div className="rv-item" key={r.id} data-testid={`rv-item-${r.id}`}>
              <div className="rv-item-head">
                <Stars n={r.rating} />
                <span className="rv-meta">{r.doctor_name} · {r.specialty}{r.kind === "clinic" ? " · Clinic" : ""}</span>
              </div>
              {r.comment && <p className="rv-comment">"{r.comment}"</p>}
              <span className="rv-by">— {r.patient_name || "Patient"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Analytics ------------------------------ */
function AdminAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(r => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return <p className="lead">Loading…</p>;
  const k = d.kpis;
  const maxRev = Math.max(1, ...d.revenue_trend.map(t => t.revenue));
  const kpiCards = [
    ["Total bookings", k.total_bookings], ["Revenue", `₹${k.revenue.toLocaleString("en-IN")}`],
    ["Completed", `${k.completed} (${k.completion_rate}%)`], ["Cancelled", `${k.cancelled} (${k.cancellation_rate}%)`],
    ["Clinic visits", k.clinic_visits], ["Avg fee", `₹${k.avg_fee}`],
    ["Patient conversion", `${k.conversion_rate}%`], ["Subscribers", k.subscribers],
    ["Reviews", `${k.reviews} · ${k.review_avg}★`], ["Clinic revenue", `₹${k.clinic_revenue.toLocaleString("en-IN")}`],
  ];
  return (
    <div data-testid="admin-analytics">
      <div className="an-kpis">
        {kpiCards.map(([l, v]) => <div className="an-kpi" key={l} data-testid={`an-kpi-${l}`}><span>{l}</span><b>{v}</b></div>)}
      </div>
      <div className="rv-grid">
        <div className="card">
          <h4 className="h" style={{ fontSize: 15, marginTop: 0 }}>Revenue — last 6 months</h4>
          <div className="an-bars">
            {d.revenue_trend.map(t => (
              <div className="an-bar-col" key={t.month}>
                <div className="an-bar" style={{ height: `${Math.round(100 * t.revenue / maxRev)}%` }} title={`₹${t.revenue}`} />
                <span>{t.month.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <h4 className="h" style={{ fontSize: 15, marginTop: 0 }}>Top specialties</h4>
          {d.top_specialties.map(s => (
            <div className="an-row" key={s.name}><span>{s.name}</span><b>{s.count}</b></div>
          ))}
          <h4 className="h" style={{ fontSize: 15, marginTop: 16 }}>Top cities (clinics)</h4>
          {d.top_cities.length === 0 && <p className="lead" style={{ fontSize: 13 }}>No approved clinics yet.</p>}
          {d.top_cities.map(c => (
            <div className="an-row" key={c.name}><span>{c.name}</span><b>{c.count}</b></div>
          ))}
        </div>
      </div>
    </div>
  );
}

const SECTIONS = { dashboard: Dashboard, patients: Patients, doctors: Doctors, approvals: Approvals, appointments: Appointments, reviews: AdminReviews, analytics: AdminAnalytics, support: CustomerSupport, security: AccountSecurity, "clinical-audit": ClinicalAuditLog, content: Content, about: AboutUs, specialties: Specialties, header: Header, comms: CommsSection, callbacks: Callbacks, audit: AuditLog, "biz-patients": BusinessPatients, "biz-doctors": BusinessDoctors, "biz-revenue": BusinessRevenue, "biz-payments": BusinessPayments, "b2b-inquiries": B2BInquiries, "b2b-partners": B2BPartners, "b2b-categories": B2BCategories, "b2b-products": B2BProducts, "b2b-revenue": B2BRevenue, "b2b-content": B2BPageContent, "b2b-orders": B2BOrders };

/* -------------------------------- Console -------------------------------- */
export default function AdminConsole() {
  const { user, logout } = useAuth();
  const { summary } = useMessaging();
  const nav = useNavigate();
  const [page, setPage] = useState("dashboard");
  const [open, setOpen] = useState(false);
  const [adminConv, setAdminConv] = useState(null);
  const [openGroups, setOpenGroups] = useState({ communications: true, biz: false, b2b: false, site: false });

  if (user === undefined) return <div className="adm"><div className="login-shell" style={{ gridColumn: "1 / -1", display: "grid", placeItems: "center", minHeight: "60vh" }}><p className="lead">Loading…</p></div></div>;
  if (!user || user.role !== "admin") return <AdminLogin onDone={() => setPage("dashboard")} />;
  if (user.must_change_password) return <ForceChangePassword />;

  const Section = SECTIONS[page];
  const title = NAV_FLAT.find(n => n.key === page)?.label;
  const openInbox = (cid) => { setAdminConv(cid); setPage("inbox"); };
  const toggleGroup = (key) => setOpenGroups((g) => ({ ...g, [key]: !g[key] }));
  const goto = (key) => { setPage(key); setOpen(false); };

  return (
    <div className="adm">
      <aside className={`side ${open ? "show" : ""}`}>
        <div className="side-brand"><div className="side-logo">2</div><div><h1>2nd Opinion</h1><p>Admin console</p></div></div>
        <nav className="nav">
          {NAV.map((n) => {
            if (n.type === "item") {
              const Icon = n.icon;
              return (
                <button key={n.key} className={page === n.key ? "on" : ""} onClick={() => goto(n.key)} data-testid={`admin-nav-${n.key}`}>
                  <Icon /> {n.label}
                </button>
              );
            }
            // group
            const Icon = n.icon;
            const isOpen = !!openGroups[n.key] || n.children.some((c) => c.key === page);
            const hasActive = n.children.some((c) => c.key === page);
            return (
              <div className={`nav-group-wrap ${isOpen ? "open" : ""}`} key={n.key}>
                <button
                  className={`nav-group-btn ${hasActive ? "has-active" : ""}`}
                  onClick={() => toggleGroup(n.key)}
                  aria-expanded={isOpen}
                  data-testid={`admin-nav-group-${n.key}`}
                >
                  <Icon /> <span>{n.label}</span>
                  <ChevronDown className="nav-chev" size={14} />
                </button>
                {isOpen && (
                  <div className="nav-group-children">
                    {n.children.map((c) => {
                      const CIcon = c.icon;
                      return (
                        <button
                          key={c.key}
                          className={page === c.key ? "on" : ""}
                          onClick={() => goto(c.key)}
                          data-testid={`admin-nav-${c.key}`}
                        >
                          <CIcon /> {c.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="side-foot">
          <a onClick={() => nav("/")}><Home size={15} /> View website</a>
          <a onClick={logout} data-testid="admin-logout"><LogOut size={15} /> Sign out</a>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="burger" onClick={() => setOpen(!open)}><MenuIcon size={18} /></button>
            <BackToHome />
            <h2>{title}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <NotificationsBell count={summary.patients} kinds={["admin_patient"]} color="#185FA5" onOpen={openInbox} testid="admin-patient-bell" label="Patient messages" />
            <NotificationsBell count={summary.doctors} kinds={["admin_doctor"]} color="#1D9E75" onOpen={openInbox} testid="admin-doctor-bell" label="Doctor messages" />
            <span className="pill">Administrator</span>
          </div>
        </div>
        <div className="content">
          {page === "inbox"
            ? <Inbox role="admin" initialConversationId={adminConv} accent="#185FA5" />
            : page === "dashboard"
              ? <Dashboard onNavigate={setPage} />
              : <Section />}
        </div>
      </div>
    </div>
  );
}
